import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@vkontakte/vkui';
import { Icon24Search, Icon24Cancel } from '@vkontakte/icons';
import DataTable from 'react-data-table-component';
import { getAccessToken, getAuthHeaders } from '../utils/authUtils';
import { hasDraft, deleteDraft } from '../utils/draftUtils';
import logoImage from '../assets/images/logo_512.png';
import '../App.css';
import TreeLoader from './common/TreeLoader';
import { API_BASE, API_V2_BASE } from '../config';
import { handbookById } from './forms/Form1/pickerHooks';
import { useReportFormPicker } from './forms/Form1/reportPickerHooks';
import PickerModal from './forms/Form1/PickerModal';
import GlpmReportParamsSection from './reports/GlpmReportParamsSection';
import UsersAdminPage from './UsersAdminPage';
import DecimalInput from './fields/DecimalInput';
import { toDecimalPoint } from '../utils/decimalUtils';

// Руководство пользователя лежит рядом с лендингом и отдаётся с того же адреса:
// обновить его — значит заменить один файл на сервере, пересборка не нужна.
const USER_GUIDE_URL = '/app/manual/user-guide.pdf';

const GLPM_MONTHLY_PLANNED_VOLUMES_TYPE = 'glpm_monthly_planned_volumes';
const GLPM_YEARLY_PLANNED_VOLUMES_TYPE = 'glpm_yearly_planned_volumes';
/** Отчёт о выполнении запланированных объёмов (без «за месяц» — сводный). */
const GLPM_PLANNED_VOLUMES_EXECUTION_TYPE = 'glpm_planned_volumes_execution';
/** Ведомость описания характеристик насаждений (report_3). */
const GLPM_WOOD_STAND_DESCRIPTION_TYPE = 'glpm_wood_stand_description';
/** Т1, МЗЛ, УПП: книга из листа «Таблица №1» и производных от него реестров. */
const TABL1_TYPE = 'tabl1';
const MZL_REGISTRY_TYPE = 'registry_mzl';
const UPP_REGISTRY_TYPE = 'registry_upp';
// Таблица № 1 и оба реестра строятся из одних карточек и одинаково выбираются
// по субъекту и году, но формируются отдельными книгами — у каждого свой адрес
const TABL1_FAMILY_ENDPOINTS = {
  [TABL1_TYPE]: { path: 'reports/tabl1/', file: 'tabl1' },
  [MZL_REGISTRY_TYPE]: { path: 'reports/registry-mzl/', file: 'reestr_mzl' },
  [UPP_REGISTRY_TYPE]: { path: 'reports/registry-upp/', file: 'reestr_upp' },
};
function isTabl1FamilyType(value) {
  return Object.prototype.hasOwnProperty.call(TABL1_FAMILY_ENDPOINTS, value);
}
// Полная выгрузка по всем субъектам и годам — только администраторам 3 и 4 уровня
function canUseTabl1FullExport(user) {
  return user?.role === 'LEVEL3' || user?.role === 'LEVEL4';
}
// Т1, МЗЛ, УПП без отбора по субъекту и году — все доступные пользователю карточки
const TABL1_FULL_TYPE = 'tabl1_full';
// Точки карточек ВНН субъекта за год — CSV в формате образца «Проверка координат»
const COORDINATES_TYPE = 'coordinates';
/** Отчётная форма ВУ-околот по карточкам ВНВО. */
const VNVO_OKOLOT_TYPE = 'vnvo_okolot';
/** Отчётная форма ВУ_кладки-ствол по карточкам ВНВО. */
const VNVO_KLADKI_STVOL_TYPE = 'vnvo_kladki_stvol';

function isGlpmParamsReportType(value) {
  return (
    value === GLPM_MONTHLY_PLANNED_VOLUMES_TYPE ||
    value === GLPM_YEARLY_PLANNED_VOLUMES_TYPE ||
    value === GLPM_PLANNED_VOLUMES_EXECUTION_TYPE ||
    value === GLPM_WOOD_STAND_DESCRIPTION_TYPE ||
    value === TABL1_TYPE ||
    value === MZL_REGISTRY_TYPE ||
    value === UPP_REGISTRY_TYPE ||
    value === COORDINATES_TYPE ||
    value === VNVO_OKOLOT_TYPE ||
    value === VNVO_KLADKI_STVOL_TYPE
  );
}

function useDebouncedValue(value, delay = 180) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// Фильтрация списка по подстроке: сначала совпадения с начала строки, потом по алфавиту.
function smartFilterList(items, searchText) {
  if (!searchText) return items;
  const searchLower = searchText.toLowerCase();
  const filtered = items.filter((item) => item.toLowerCase().includes(searchLower));
  return filtered.sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aStartsWith = aLower.startsWith(searchLower);
    const bStartsWith = bLower.startsWith(searchLower);
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return aLower.localeCompare(bLower);
  });
}

/**
 * Один список с чекбоксами в фильтре адреса (Субъект, Лесничество и т.д.).
 * Поле поиска хранит состояние ЛОКАЛЬНО, а сам компонент обёрнут в React.memo —
 * поэтому ввод текста перерисовывает только этот список, мгновенно, и не трогает
 * ни таблицу, ни остальные четыре списка.
 */
const AddressFilterList = React.memo(function AddressFilterList({
  title, placeholder, part, options, selected, onChangePart, emptyText,
}) {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebouncedValue(searchText);
  const filtered = React.useMemo(
    () => smartFilterList(options, debouncedSearchText),
    [options, debouncedSearchText],
  );
  const allSelected = filtered.length > 0 && filtered.every((s) => selected.includes(s));

  return (
    <div style={{
      backgroundColor: '#fafafa',
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      padding: '12px',
      transition: 'all 0.2s ease',
      minHeight: '250px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
        {title}
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '0.9rem',
          border: '1px solid #ddd',
          borderRadius: '6px',
          boxSizing: 'border-box',
          backgroundColor: 'white',
          color: '#495057',
          outline: 'none',
          transition: 'all 0.2s ease',
          marginBottom: '8px',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#adb5bd';
          e.currentTarget.style.backgroundColor = '#e8ecef';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#ddd';
          e.currentTarget.style.backgroundColor = 'white';
        }}
      />
      {filtered.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer', marginBottom: '4px' }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                onChangePart(part, [...new Set([...selected, ...filtered])]);
              } else {
                onChangePart(part, selected.filter((s) => !filtered.includes(s)));
              }
            }}
            style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.85rem', color: '#495057' }}>Выбрать все ({filtered.length})</span>
        </label>
      )}
      <div
        className="custom-scrollbar"
        style={{
          maxHeight: '150px',
          overflowY: 'auto',
          overflowX: 'hidden',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '4px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e0 #f1f5f9',
        }}
      >
        {filtered.length > 0 ? filtered.map((option, idx) => (
          <label key={idx} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) => {
                if (e.target.checked) {
                  onChangePart(part, [...selected, option]);
                } else {
                  onChangePart(part, selected.filter((s) => s !== option));
                }
              }}
              style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.85rem', color: '#495057' }}>{option}</span>
          </label>
        )) : (
          <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>
            {debouncedSearchText ? 'Ничего не найдено' : emptyText}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#495057' }}>
          Выбрано: {selected.length}
        </div>
      )}
    </div>
  );
});

/** ФИО исполнителя в журнале видят те, кому видны чужие карточки: филиал и администраторы. */
const roleSeesExecutor = (role) => role === 'LEVEL2' || role === 'LEVEL3' || role === 'LEVEL4';

const CHECKLIST_CHECKBOX_STYLE = {
  marginRight: '8px',
  cursor: 'pointer',
  width: '18px',
  height: '18px',
  minWidth: '18px',
  minHeight: '18px',
  flexShrink: 0,
};

const checklistRowHover = {
  onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; },
  onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; },
};

/**
 * Окошко фильтра «список с галочками» для колонки журнала — то же, что у «Причины»
 * и «Породы»: поиск, «Выбрать все», список и счётчик выбранного. Текст поиска живёт
 * внутри: при закрытии окошка он сбрасывается сам, а набор букв не перерисовывает
 * таблицу.
 */
const ChecklistFilter = React.memo(function ChecklistFilter({
  options, selected, onChange, placeholder, emptyText,
}) {
  const [searchText, setSearchText] = useState('');
  const filtered = React.useMemo(
    () => smartFilterList(options, searchText),
    [options, searchText],
  );
  const allSelected = filtered.length > 0 && filtered.every((item) => selected.includes(item));

  return (
    <div>
      <input
        type="text"
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '0.9rem',
          border: '1px solid #ddd',
          borderRadius: '6px',
          boxSizing: 'border-box',
          backgroundColor: 'white',
          color: '#495057',
          outline: 'none',
          transition: 'all 0.2s ease',
          marginBottom: '12px',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#adb5bd';
          e.currentTarget.style.backgroundColor = '#e8ecef';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#ddd';
          e.currentTarget.style.backgroundColor = 'white';
        }}
      />
      {filtered.length > 0 && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
            marginBottom: '8px',
            borderBottom: '1px solid #ddd',
            paddingBottom: '12px',
            fontWeight: '500',
          }}
          {...checklistRowHover}
        >
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => onChange(
              e.target.checked
                ? [...new Set([...selected, ...filtered])]
                : selected.filter((item) => !filtered.includes(item)),
            )}
            style={CHECKLIST_CHECKBOX_STYLE}
          />
          <span style={{ fontSize: '0.9rem', color: '#495057', fontWeight: '500' }}>
            Выбрать все ({filtered.length})
          </span>
        </label>
      )}
      <div
        className="custom-scrollbar"
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          overflowX: 'hidden',
          border: '1px solid #ddd',
          borderRadius: '6px',
          backgroundColor: 'white',
          padding: '8px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e0 #f1f5f9',
        }}
      >
        {filtered.length > 0 ? filtered.map((item) => (
          <label
            key={item}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              marginBottom: '4px',
            }}
            {...checklistRowHover}
          >
            <input
              type="checkbox"
              checked={selected.includes(item)}
              onChange={(e) => onChange(
                e.target.checked ? [...selected, item] : selected.filter((s) => s !== item),
              )}
              style={CHECKLIST_CHECKBOX_STYLE}
            />
            <span style={{ fontSize: '0.9rem', color: '#495057' }}>{item}</span>
          </label>
        )) : (
          <div style={{ padding: '8px', color: '#999', fontSize: '0.9rem' }}>
            {searchText ? 'Ничего не найдено' : emptyText}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div style={{
          marginTop: '12px',
          fontSize: '0.85rem',
          color: '#495057',
          padding: '8px',
          backgroundColor: '#e8ecef',
          borderRadius: '4px',
        }}>
          Выбрано: {selected.length}
        </div>
      )}
    </div>
  );
});

/** База API (как в запросах отчётов на этой странице). */
const GLPM_API_BASE = API_BASE;
/** База API v2 (документы/согласование) — версия вынесена в config.js, здесь не дублируем. */
const GLPM_API_V2_BASE = API_V2_BASE;

/** Ключ localStorage для сохранения выбранного числа строк на странице журнала. */
const PER_PAGE_STORAGE_KEY = 'documents_per_page';

/** Ключ localStorage для сохранения текущей страницы журнала. */
const PAGE_STORAGE_KEY = 'documents_page';

/** Текущий календарный месяц в формате YYYY-MM (для сбросов/подсказок при необходимости). */
function getCurrentMonthYYYYMM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getTodayReportDate() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/** Виды отчётов для формирования (страница «Меню работы с отчётами») */
// Отчёты разнесены по подгруппам: ВНН — по карточкам ВНН, ВНВО — по карточкам
// наблюдения за вредными организмами. Подгруппа выводится заголовком в списке.
const REPORT_FORM_TYPE_OPTIONS = [
  { value: '', label: '— Выберите вид отчёта —' },
  {
    value: GLPM_MONTHLY_PLANNED_VOLUMES_TYPE,
    group: 'ВНН',
    label:
      'Отчет о выполнении запланированных объёмов работ по государственному лесопатологическому мониторингу за месяц',
  },
  {
    value: GLPM_YEARLY_PLANNED_VOLUMES_TYPE,
    group: 'ВНН',
    label:
      'Отчет о выполнении запланированных объёмов работ по государственному лесопатологическому мониторингу за год',
  },
  {
    value: GLPM_PLANNED_VOLUMES_EXECUTION_TYPE,
    group: 'ВНН',
    label:
      'Отчет о выполнении запланированных объёмов работ по государственному лесопатологическому мониторингу за квартал',
  },
  {
    value: GLPM_WOOD_STAND_DESCRIPTION_TYPE,
    group: 'ВНН',
    label:
      'Ведомость описания характеристик насаждений по результатам государственного лесопатологического мониторинга в лесах',
  },
  {
    value: TABL1_TYPE,
    group: 'ВНН',
    label: 'Таблица № 1. Сведения о санитарном и лесопатологическом состоянии насаждений',
  },
  {
    value: MZL_REGISTRY_TYPE,
    group: 'ВНН',
    label: 'Реестр лесных участков, на которых рекомендуется проведение мероприятий по защите лесов (Реестр МЗЛ-в)',
  },
  {
    value: UPP_REGISTRY_TYPE,
    group: 'ВНН',
    label: 'Реестр лесных участков, занятых поврежденными и погибшими лесными насаждениями (Реестр УПП-в)',
  },
  {
    value: TABL1_FULL_TYPE,
    group: 'ВНН',
    label: 'Т1, МЗЛ, УПП — полная выгрузка',
  },
  {
    value: COORDINATES_TYPE,
    group: 'ВНН',
    label: 'Координаты',
  },
  {
    value: VNVO_OKOLOT_TYPE,
    group: 'ВНВО',
    label: 'Отчётная форма ВУ-околот',
  },
  {
    value: VNVO_KLADKI_STVOL_TYPE,
    group: 'ВНВО',
    label: 'Отчётная форма ВУ_кладки-ствол',
  },
];

/** Пользователю уровня 1 — только отчёты о выполнении запланированных объёмов,
 *  по своим карточкам (сервер проверяет то же: catalogs/access.CanCreateReports). */
const LEVEL1_REPORT_TYPES = [
  GLPM_MONTHLY_PLANNED_VOLUMES_TYPE,
  GLPM_PLANNED_VOLUMES_EXECUTION_TYPE,
  GLPM_YEARLY_PLANNED_VOLUMES_TYPE,
];

function getReportFormTypeLabel(value) {
  const found = REPORT_FORM_TYPE_OPTIONS.find((o) => o.value === value);
  return found ? found.label : '';
}

function parseReportPreviewHeader(response) {
  const raw = response.headers.get('X-GLPM-Report-Preview');
  if (!raw) return null;
  try {
    const bytes = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch (e) {
    console.warn('X-GLPM-Report-Preview', e, raw);
    return null;
  }
}

function getResponseFilename(response, fallbackName) {
  const cd = response.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1].trim() : fallbackName;
}

function ReportPreview({ report }) {
  if (!report) return null;
  const preview = report.preview || {};

  if (preview.kind === 'excel_planned_volumes') {
    return (
      <div className="report-preview">
        <div className="report-preview-header">
          <h4>{preview.title || report.filename}</h4>
          <span>{report.filename}</span>
        </div>
        <table className="report-preview-table report-preview-meta">
          <tbody>
            <tr>
              <td>Филиал</td>
              <td>{preview.branch_name || '—'}</td>
            </tr>
            <tr>
              <td>Субъект РФ</td>
              <td>{preview.region || '—'}</td>
            </tr>
            <tr>
              <td>{preview.period_label || 'Период'}</td>
              <td>{preview.period_value || '—'}</td>
            </tr>
          </tbody>
        </table>
        <table className="report-preview-table">
          <thead>
            <tr>
              <th>Наименование способа осуществления ГЛПМ</th>
              <th>Единица измерения</th>
              <th>{preview.plan_label || 'План'}</th>
              <th>Выполнение</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Выборочные наземные наблюдения за санитарным и лесопатологическим состоянием лесов</td>
              <td>га</td>
              <td className="report-preview-value">{preview.plan_ha ?? '—'}</td>
              <td className="report-preview-value">{preview.lp ?? '—'}</td>
            </tr>
            <tr>
              <td></td>
              <td>км</td>
              <td>-</td>
              <td className="report-preview-value">{preview.km ?? '—'}</td>
            </tr>
          </tbody>
        </table>
        <table className="report-preview-table report-preview-sign">
          <tbody>
            <tr>
              <td>Исполнитель</td>
              <td>{preview.executor || '—'}</td>
            </tr>
            <tr>
              <td>Телефон</td>
              <td>{preview.number || '—'}</td>
            </tr>
            <tr>
              <td>Дата</td>
              <td>{preview.date || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (preview.kind === 'word_planned_volumes_execution') {
    const months = Array.isArray(preview.months) ? preview.months : [];
    return (
      <div className="report-preview">
        <div className="report-preview-header">
          <h4>{preview.title || report.filename}</h4>
          <span>{report.filename}</span>
        </div>
        <table className="report-preview-table report-preview-meta">
          <tbody>
            <tr>
              <td>Филиал</td>
              <td>{preview.branch_name || '—'}</td>
            </tr>
            <tr>
              <td>Субъект РФ</td>
              <td>{preview.region || '—'}</td>
            </tr>
            <tr>
              <td>Квартал</td>
              <td>{preview.quarter || '—'}</td>
            </tr>
            <tr>
              <td>Год</td>
              <td>{preview.year || '—'}</td>
            </tr>
          </tbody>
        </table>
        <table className="report-preview-table">
          <thead>
            <tr>
              <th colSpan={3 + months.length}>
                Выборочные наземные наблюдения за санитарным и лесопатологическим состоянием лесов
              </th>
            </tr>
            <tr>
              <th>Годовой план</th>
              <th>Квартальный план</th>
              {months.map((m, index) => (
                <th key={index}>{m.name || `Месяц ${index + 1}`}</th>
              ))}
              <th>Итого</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="report-preview-value">{preview.god_plan || '—'}</td>
              <td className="report-preview-value">{preview.kvart_plan || '—'}</td>
              {months.map((m, index) => (
                <td key={index} className="report-preview-value">{m.value || '—'}</td>
              ))}
              <td className="report-preview-value">{preview.sum || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (preview.kind === 'excel_wood_stand_description') {
    const rows = Array.isArray(preview.rows) ? preview.rows : [];
    const num = (v) => (v === null || v === undefined || v === '' ? '—' : v);
    const sum = (key) => {
      const total = rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
      return Math.round(total * 100) / 100;
    };
    const dataCols = ['vsego', 'sani_total', 'stable', 'disturbed', 'lost', 'path_total', 'weak', 'medium', 'strong', 'complete'];
    // Названия целей приходят отдельным списком, а в строке — их номер: предпросмотр
    // едет в заголовке ответа, и повторять длинное название в каждой строке накладно.
    const goalName = (row) => {
      if (typeof row.goal === 'number') {
        return (Array.isArray(preview.goals) ? preview.goals[row.goal] : '') || '—';
      }
      return row.goal || '—';
    };
    return (
      <div className="report-preview">
        <div className="report-preview-header">
          <h4>{preview.title || report.filename}</h4>
          <span>{report.filename}</span>
        </div>
        <table className="report-preview-table report-preview-meta">
          <tbody>
            <tr>
              <td>Филиал</td>
              <td>{preview.branch_name || '—'}</td>
            </tr>
            <tr>
              <td>Субъект РФ</td>
              <td>{preview.region || '—'}</td>
            </tr>
            <tr>
              <td>Год</td>
              <td>{preview.year || '—'}</td>
            </tr>
            <tr>
              <td>{preview.period_label || 'Период'}</td>
              <td>{preview.period_value || '—'}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ overflowX: 'auto' }}>
          <table className="report-preview-table">
            <thead>
              <tr>
                <th rowSpan={3}>Группа причин повреждения</th>
                <th rowSpan={3}>Цель работ</th>
                <th rowSpan={3}>Единица измерения</th>
                <th rowSpan={3}>Объём работ (всего)</th>
                <th colSpan={9}>Результаты работ</th>
              </tr>
              <tr>
                <th colSpan={4}>Наблюдения за санитарным состоянием лесов, га</th>
                <th colSpan={5}>Наблюдения за лесопатологическим состоянием лесов, га</th>
              </tr>
              <tr>
                <th>всего</th>
                <th>устойчивые</th>
                <th>с нарушенной устойчивостью</th>
                <th>с утраченной устойчивостью</th>
                <th>всего</th>
                <th>слабое (до 25%)</th>
                <th>среднее (26–49%)</th>
                <th>сильное (50–75%)</th>
                <th>сплошное (более 75%)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center' }}>
                    {preview.rows_omitted
                      ? 'Таблица слишком велика для предпросмотра — откройте скачанный файл'
                      : 'Нет данных за выбранный период'}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i}>
                    <td className="report-preview-value">{num(r.group)}</td>
                    <td>{goalName(r)}</td>
                    <td className="report-preview-value">{r.unit || 'га'}</td>
                    <td className="report-preview-value">{num(r.vsego)}</td>
                    <td className="report-preview-value">{num(r.sani_total)}</td>
                    <td className="report-preview-value">{num(r.stable)}</td>
                    <td className="report-preview-value">{num(r.disturbed)}</td>
                    <td className="report-preview-value">{num(r.lost)}</td>
                    <td className="report-preview-value">{num(r.path_total)}</td>
                    <td className="report-preview-value">{num(r.weak)}</td>
                    <td className="report-preview-value">{num(r.medium)}</td>
                    <td className="report-preview-value">{num(r.strong)}</td>
                    <td className="report-preview-value">{num(r.complete)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 600, textAlign: 'center' }}>Всего</td>
                  {dataCols.map((key) => (
                    <td key={key} className="report-preview-value" style={{ fontWeight: 600 }}>{sum(key)}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="report-preview">
      <div className="report-preview-header">
        <h4>Отчёт сформирован</h4>
      </div>
      <div>{report.filename}</div>
    </div>
  );
}

/** Те же inline-стили, что в `Form1.js` у `template-header` */
const FORM1_TEMPLATE_HEADER_STYLE = {
  cursor: 'pointer',
  padding: '10px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

/** Обёртка `reference-cell`, как в `DetailsSection.js` */
const FORM1_REFERENCE_CELL_OUTER = {
  minHeight: 60,
  height: 'auto',
  cursor: 'pointer',
  background: 'transparent',
};

/** `textarea` / поле внутри `reference-cell` — как в `DetailsSection.js` */
const FORM1_REFERENCE_FIELD_INNER = {
  border: 'none',
  outline: 'none',
  resize: 'none',
  background: 'none',
  borderRadius: '0',
  minHeight: 60,
  height: 'auto',
  textAlign: 'center',
  width: '100%',
  overflow: 'hidden',
  padding: '6px 0',
  boxSizing: 'border-box',
  cursor: 'pointer',
};

const DocumentsPage = ({ 
  onSelectDocument, 
  onCreateDocument,
  onRestoreDraft,
  user,
  onLogout,
  listHeading = 'Журнал карточек ВНН',
  showJournalTable = true,
  adminUsersMode = false,
  handbooks = [],
  canCreateReports = false,
  canManagePlans = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Состояние для загрузки отчётов из API (серверная пагинация)
  const [reports, setReports] = useState([]);          // карточки текущей страницы
  const [dupTooltip, setDupTooltip] = useState(null);  // подсказка над карточкой-дубликатом { x, y }
  const [totalRows, setTotalRows] = useState(0);        // всего карточек после фильтрации
  // Текущую страницу храним там же и по той же причине, что и число строк:
  // открытие карточки размонтирует журнал, и филиал, проверявший карточки
  // на седьмой странице, возвращался работать на первую.
  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const stored = Number(window.localStorage.getItem(PAGE_STORAGE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : 1;
  });
  // Число строк на странице сохраняем в localStorage: DocumentsPage размонтируется
  // при открытии карточки (App.js рендерит либо журнал, либо редактор, но не оба
  // сразу), и без этого выбор пользователя (10/20/50/100) слетал бы на 20 при возврате.
  const [perPage, setPerPage] = useState(() => {
    if (typeof window === 'undefined') return 20;
    const stored = Number(window.localStorage.getItem(PER_PAGE_STORAGE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : 20;
  });
  // Серверная сортировка: поле и направление сортировки, применённые на бэкенде.
  // null — сортировка не задана, бэкенд отдаёт данные в порядке по умолчанию (журнал «наоборот»).
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  // Счётчик для принудительной перезагрузки журнала с сервера (например, после
  // «Вернуть» — карточка может пропасть из выдачи для текущей роли, локальный
  // патч состояния этого не отражает).
  const [reloadToken, setReloadToken] = useState(0);
  const [serverOptions, setServerOptions] = useState({
    reasons: [], species: [], executors: [], subjects: [], forestries: [],
    district_forestries: [], kvs: [], vids: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // searchInput — черновой текст в поле; searchText — применённый запрос (по кнопке «Поиск»/Enter),
  // именно он запускает серверный запрос и фильтрацию, чтобы не дёргать сервер на каждое нажатие.
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const applySearch = () => {
    setSearchText(searchInput.trim());
    setCurrentPage(1);
  };
  // Переключатель сброса пагинации на первую страницу при изменении поиска/фильтров
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  // Массовое одобрение (уровни 2 и 3): выбранные на текущей странице карточки
  const [selectedForApproval, setSelectedForApproval] = useState([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [selectedReportFormType, setSelectedReportFormType] = useState('');
  const [reportFormComboOpen, setReportFormComboOpen] = useState(false);
  const [reportFormFilter, setReportFormFilter] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const reportFormComboRef = useRef(null);

  const filteredReportFormOptions = React.useMemo(() => {
    const q = reportFormFilter.trim().toLowerCase();
    const canUseFullExport = canUseTabl1FullExport(user);
    const level1 = user?.role === 'LEVEL1';
    return REPORT_FORM_TYPE_OPTIONS.filter(
      (opt) => (opt.value !== TABL1_FULL_TYPE || canUseFullExport)
        && (!level1 || !opt.value || LEVEL1_REPORT_TYPES.includes(opt.value))
        && (!q || opt.label.toLowerCase().includes(q))
    );
  }, [reportFormFilter, user]);

  useEffect(() => {
    if (!reportFormComboOpen) return;
    const onDocMouseDown = (e) => {
      if (reportFormComboRef.current && !reportFormComboRef.current.contains(e.target)) {
        setReportFormComboOpen(false);
        setReportFormFilter('');
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setReportFormComboOpen(false);
        setReportFormFilter('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [reportFormComboOpen]);

  useEffect(() => {
    return () => {
      if (generatedReport?.url) {
        URL.revokeObjectURL(generatedReport.url);
      }
    };
  }, [generatedReport?.url]);

  const setPreparedReport = React.useCallback((report) => {
    setGeneratedReport((prev) => {
      if (prev?.url) {
        URL.revokeObjectURL(prev.url);
      }
      return report;
    });
  }, []);

  const clearGeneratedReport = React.useCallback(() => {
    setPreparedReport(null);
  }, [setPreparedReport]);

  const downloadGeneratedReport = React.useCallback(() => {
    if (!generatedReport?.url) return;
    const a = document.createElement('a');
    a.href = generatedReport.url;
    a.download = generatedReport.filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [generatedReport]);

  const prepareGeneratedReport = React.useCallback(async (response, reportType, fallbackName, fallbackPreview) => {
    const blob = await response.blob();
    const filename = getResponseFilename(response, fallbackName);
    const url = URL.createObjectURL(blob);
    setPreparedReport({
      reportType,
      filename,
      url,
      blob,
      preview: parseReportPreviewHeader(response) || fallbackPreview || null,
    });
  }, [setPreparedReport]);

  const [glpmMonthlyReportMonth, setGlpmMonthlyReportMonth] = useState('');
  const [glpmMonthlyGenerating, setGlpmMonthlyGenerating] = useState(false);
  const [glpmYearlyGenerating, setGlpmYearlyGenerating] = useState(false);
  const [glpmExecutionGenerating, setGlpmExecutionGenerating] = useState(false);
  const [glpmPickerFormData, setGlpmPickerFormData] = useState({ srf: '', filial: '' });
  const [glpmYearlyReportYear, setGlpmYearlyReportYear] = useState(() => new Date().getFullYear());
  const [glpmExecutionQuarter, setGlpmExecutionQuarter] = useState('');
  const [glpmExecutionYear, setGlpmExecutionYear] = useState(() => new Date().getFullYear());
  const [glpmWoodStandYear, setGlpmWoodStandYear] = useState(() => new Date().getFullYear());
  // Отчётный год ВУ-околот: отбор по году даты проведения работ.
  const [vnvoOkolotYear, setVnvoOkolotYear] = useState(() => new Date().getFullYear());
  const [vnvoOkolotDownloading, setVnvoOkolotDownloading] = useState(false);
  // Отчётный год ВУ_кладки-ствол: отбор по году даты проведения работ.
  const [vnvoKladkiYear, setVnvoKladkiYear] = useState(() => new Date().getFullYear());
  const [vnvoKladkiDownloading, setVnvoKladkiDownloading] = useState(false);
  // Отчётный год Таблицы №1: отбираются карточки по году даты проведения ВНН.
  const [tabl1Year, setTabl1Year] = useState(() => new Date().getFullYear());
  const [coordinatesYear, setCoordinatesYear] = useState(() => new Date().getFullYear());
  const [coordinatesDownloading, setCoordinatesDownloading] = useState(false);
  const [glpmWoodStandDownloading, setGlpmWoodStandDownloading] = useState(false);
  const [tabl1Downloading, setTabl1Downloading] = useState(false);
  const [tabl1FullDownloading, setTabl1FullDownloading] = useState(false);

  // Таблица "Наблюдения" для ведомости характеристик насаждений.
  // В генерации сейчас фиктивно: нажатие кнопки ничего не делает,
  // но параметры нужно собрать в UI.
  // Субъекты РФ, доступные пользователю по его филиалу (null — без ограничения / ещё не загружено).
  const [allowedSubjectIds, setAllowedSubjectIds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${GLPM_API_BASE}/reports/allowed-subjects/`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) return;
        const list = await response.json();
        if (cancelled || !Array.isArray(list)) return;
        setAllowedSubjectIds(new Set(list.map((s) => String(s.id))));
      } catch (e) {
        // при ошибке оставляем null — пикер покажет все субъекты
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setGlpmPickerLocal = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    setGlpmPickerFormData((prev) => ({
      srf: data.srf !== undefined ? data.srf : prev.srf,
      filial: data.filial !== undefined ? data.filial : prev.filial,
    }));
  }, []);

  const {
    showPicker,
    pickerSearch,
    setPickerSearch,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    pickerValue,
    selectPickerValue,
    closePicker,
    openPicker,
  } = useReportFormPicker(handbooks || [], glpmPickerFormData, setGlpmPickerLocal, allowedSubjectIds);

  const glpmEffectiveBranchName = React.useMemo(
    () => (user?.role === 'LEVEL3' ? 'Администратор' : String(glpmPickerFormData.filial || '').trim()),
    [user?.role, glpmPickerFormData.filial]
  );

  useEffect(() => {
    clearGeneratedReport();
  }, [
    selectedReportFormType,
    glpmMonthlyReportMonth,
    glpmYearlyReportYear,
    glpmExecutionQuarter,
    glpmExecutionYear,
    glpmEffectiveBranchName,
    glpmPickerFormData.srf,
    clearGeneratedReport,
  ]);

  const clearReportFormTypeBlock = React.useCallback(() => {
    setSelectedReportFormType('');
    setGlpmMonthlyReportMonth('');
    setGlpmPickerFormData({ srf: '', filial: '' });
    setGlpmYearlyReportYear(new Date().getFullYear());
    setGlpmExecutionQuarter('');
    setGlpmExecutionYear(new Date().getFullYear());
    setGlpmWoodStandYear(new Date().getFullYear());
    clearGeneratedReport();
    setReportFormComboOpen(false);
    setReportFormFilter('');
  }, [clearGeneratedReport]);

  const clearGlpmExecutionPeriod = React.useCallback(() => {
    setGlpmExecutionQuarter('');
    setGlpmExecutionYear(new Date().getFullYear());
  }, []);

  const clearGlpmYearlyPeriod = React.useCallback(() => {
    setGlpmYearlyReportYear(new Date().getFullYear());
  }, []);

  const clearGlpmMonthlyParams = React.useCallback(() => {
    setGlpmMonthlyReportMonth('');
    setGlpmPickerFormData({ srf: '', filial: '' });
    clearGeneratedReport();
  }, [clearGeneratedReport]);

  const glpmReportParamsBase =
    Boolean(String(glpmPickerFormData.srf || '').trim());

  const glpmReportParamsComplete =
    glpmReportParamsBase && Boolean(glpmMonthlyReportMonth);

  const canSubmitGlpmMonthlyReport =
    selectedReportFormType === GLPM_MONTHLY_PLANNED_VOLUMES_TYPE && glpmReportParamsComplete;

  const canSubmitGlpmYearlyReport =
    selectedReportFormType === GLPM_YEARLY_PLANNED_VOLUMES_TYPE &&
    glpmReportParamsBase &&
    Boolean(glpmYearlyReportYear);

  const glpmExecutionPeriodComplete =
    ['1', '2', '3', '4'].includes(String(glpmExecutionQuarter)) &&
    Boolean(glpmExecutionYear);

  const canSubmitGlpmPlannedVolumesExecution =
    selectedReportFormType === GLPM_PLANNED_VOLUMES_EXECUTION_TYPE &&
    glpmReportParamsBase &&
    glpmExecutionPeriodComplete;

  const glpmReportParamsSectionProps = {
    handbooks,
    glpmPickerFormData,
    openPicker,
    month: glpmMonthlyReportMonth,
    onMonthChange: setGlpmMonthlyReportMonth,
    maxMonth: getCurrentMonthYYYYMM(),
    onClearParams: clearGlpmMonthlyParams,
  };

  const clearGlpmExecutionReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGlpmExecutionPeriod();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGlpmExecutionPeriod, clearGeneratedReport]);

  const clearGlpmYearlyReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGlpmYearlyPeriod();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGlpmYearlyPeriod, clearGeneratedReport]);

  const clearGlpmWoodStandReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    setGlpmWoodStandYear(new Date().getFullYear());
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGeneratedReport]);

  const handleGlpmWoodStandGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== GLPM_WOOD_STAND_DESCRIPTION_TYPE) return;
    if (!String(glpmPickerFormData.srf || '').trim() || !glpmWoodStandYear) return;
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setGlpmWoodStandDownloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/reports/glpm-wood-stand-description/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ region: regionPayload, year: glpmWoodStandYear }),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, GLPM_WOOD_STAND_DESCRIPTION_TYPE, `glpm_wood_stand_description_${glpmWoodStandYear}.xlsx`, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(GLPM_WOOD_STAND_DESCRIPTION_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setGlpmWoodStandDownloading(false);
    }
  }, [selectedReportFormType, glpmPickerFormData.srf, glpmWoodStandYear, handbooks, prepareGeneratedReport]);

  const canGenerateGlpmWoodStand =
    selectedReportFormType === GLPM_WOOD_STAND_DESCRIPTION_TYPE &&
    Boolean(String(glpmPickerFormData.srf || '').trim()) &&
    Boolean(glpmWoodStandYear);

  const clearVnvoOkolotReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGeneratedReport]);

  const handleVnvoOkolotGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== VNVO_OKOLOT_TYPE) return;
    if (!String(glpmPickerFormData.srf || '').trim()) return;
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setVnvoOkolotDownloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/reports/vnvo-okolot/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ region: regionPayload, year: vnvoOkolotYear }),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, VNVO_OKOLOT_TYPE, `vu_okolot_${vnvoOkolotYear}.xlsx`, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(VNVO_OKOLOT_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setVnvoOkolotDownloading(false);
    }
  }, [selectedReportFormType, glpmPickerFormData.srf, handbooks, prepareGeneratedReport, vnvoOkolotYear]);

  const canGenerateVnvoOkolot =
    selectedReportFormType === VNVO_OKOLOT_TYPE &&
    Boolean(String(glpmPickerFormData.srf || '').trim()) &&
    Boolean(vnvoOkolotYear);

  const clearVnvoKladkiReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGeneratedReport]);

  const handleVnvoKladkiGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== VNVO_KLADKI_STVOL_TYPE) return;
    if (!String(glpmPickerFormData.srf || '').trim()) return;
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setVnvoKladkiDownloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/reports/vnvo-kladki-stvol/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ region: regionPayload, year: vnvoKladkiYear }),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, VNVO_KLADKI_STVOL_TYPE, `vu_kladki_stvol_${vnvoKladkiYear}.xlsx`, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(VNVO_KLADKI_STVOL_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setVnvoKladkiDownloading(false);
    }
  }, [selectedReportFormType, glpmPickerFormData.srf, handbooks, prepareGeneratedReport, vnvoKladkiYear]);

  const canGenerateVnvoKladki =
    selectedReportFormType === VNVO_KLADKI_STVOL_TYPE &&
    Boolean(String(glpmPickerFormData.srf || '').trim()) &&
    Boolean(vnvoKladkiYear);

  const clearTabl1Report = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGeneratedReport]);

  const handleTabl1GenerateReport = React.useCallback(async () => {
    if (!isTabl1FamilyType(selectedReportFormType)) return;
    if (!String(glpmPickerFormData.srf || '').trim()) return;
    const reportType = selectedReportFormType;
    const endpoint = TABL1_FAMILY_ENDPOINTS[reportType];
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setTabl1Downloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/${endpoint.path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ region: regionPayload, year: tabl1Year }),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, reportType, `${endpoint.file}_${tabl1Year}.xlsx`, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(reportType),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setTabl1Downloading(false);
    }
  }, [selectedReportFormType, glpmPickerFormData.srf, handbooks, prepareGeneratedReport, tabl1Year]);

  const clearCoordinatesReport = React.useCallback(() => {
    clearGlpmMonthlyParams();
    clearGeneratedReport();
  }, [clearGlpmMonthlyParams, clearGeneratedReport]);

  const handleCoordinatesGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== COORDINATES_TYPE) return;
    if (!String(glpmPickerFormData.srf || '').trim()) return;
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setCoordinatesDownloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/reports/coordinates/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ region: regionPayload, year: coordinatesYear }),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, COORDINATES_TYPE, `koordinaty_${coordinatesYear}.csv`, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(COORDINATES_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setCoordinatesDownloading(false);
    }
  }, [selectedReportFormType, glpmPickerFormData.srf, handbooks, prepareGeneratedReport, coordinatesYear]);

  const canGenerateCoordinates =
    selectedReportFormType === COORDINATES_TYPE &&
    Boolean(String(glpmPickerFormData.srf || '').trim()) &&
    Boolean(coordinatesYear);

  // Полная выгрузка параметров не имеет: сервер сам отдаёт всё, что доступно
  // пользователю по его уровню, за все годы
  const handleTabl1FullGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== TABL1_FULL_TYPE) return;
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }

    setTabl1FullDownloading(true);
    try {
      const response = await fetch(`${GLPM_API_BASE}/reports/tabl1-full/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch { /* ignore */ }
        window.alert(msg);
        return;
      }
      await prepareGeneratedReport(response, TABL1_FULL_TYPE, 'tabl1_full.xlsx', {
        kind: 'generated_file',
        title: getReportFormTypeLabel(TABL1_FULL_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setTabl1FullDownloading(false);
    }
  }, [selectedReportFormType, prepareGeneratedReport]);

  const canGenerateTabl1 =
    isTabl1FamilyType(selectedReportFormType) &&
    Boolean(String(glpmPickerFormData.srf || '').trim()) &&
    Boolean(tabl1Year);

  const handleGlpmPlannedVolumesExecutionGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== GLPM_PLANNED_VOLUMES_EXECUTION_TYPE) return;
    if (!glpmReportParamsBase || !glpmExecutionPeriodComplete) return;

    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setGlpmExecutionGenerating(true);
    try {
      const response = await fetch(
        `${GLPM_API_BASE}/reports/glpm-planned-volumes-execution/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            region: regionPayload,
            year: glpmExecutionYear,
            quarter: Number(glpmExecutionQuarter),
            date: getTodayReportDate(),
            branch_name: glpmEffectiveBranchName,
          }),
        }
      );
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch {
          /* ignore */
        }
        window.alert(msg);
        return;
      }
      const fallbackName = `glpm_planned_volumes_q${glpmExecutionQuarter}_${glpmExecutionYear}.docx`;
      await prepareGeneratedReport(response, GLPM_PLANNED_VOLUMES_EXECUTION_TYPE, fallbackName, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(GLPM_PLANNED_VOLUMES_EXECUTION_TYPE),
      });
    } catch {
      window.alert('Ошибка при формировании отчёта');
    } finally {
      setGlpmExecutionGenerating(false);
    }
  }, [
    selectedReportFormType,
    glpmReportParamsBase,
    glpmExecutionPeriodComplete,
    glpmExecutionQuarter,
    glpmExecutionYear,
    glpmPickerFormData.srf,
    handbooks,
    glpmEffectiveBranchName,
    prepareGeneratedReport,
  ]);

  const handleGlpmMonthlyGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== GLPM_MONTHLY_PLANNED_VOLUMES_TYPE) return;
    // Филиал в форме не выбирается — если он пуст, бэкенд подставит филиал
    // из профиля пользователя, поэтому branch_name не блокирует формирование.
    if (
      !glpmMonthlyReportMonth ||
      !String(glpmPickerFormData.srf || '').trim()
    ) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setGlpmMonthlyGenerating(true);
    try {
      const response = await fetch(
        `${GLPM_API_BASE}/reports/glpm-monthly-planned-volumes/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            region: regionPayload,
            month: glpmMonthlyReportMonth,
            date: getTodayReportDate(),
            branch_name: glpmEffectiveBranchName,
          }),
        }
      );
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch {
          /* ignore */
        }
        window.alert(msg);
        return;
      }
      const fallbackName = `glpm_planned_volumes_${glpmMonthlyReportMonth}.xlsx`;
      await prepareGeneratedReport(response, GLPM_MONTHLY_PLANNED_VOLUMES_TYPE, fallbackName, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(GLPM_MONTHLY_PLANNED_VOLUMES_TYPE),
      });

      const aggHdr = response.headers.get('X-GLPM-Aggregation');
      if (aggHdr) {
        try {
          const d = JSON.parse(aggHdr);
          const pad = (n) => String(n).padStart(2, '0');
          const ym = d.year != null && d.month != null ? `${d.year}-${pad(d.month)}` : glpmMonthlyReportMonth;
          let text = `ГЛПМ — сводка по данным карточек (месяц как в колонке «Дата» журнала: ${ym})\n`;
          text += `Пользователь: ${d.username || '—'}\n`;
          text += `Найдено карточек за месяц: ${d.reports_in_month ?? '—'}\n`;
          text += `В файл подставлено: lp (сумма slp) = ${d.lp_sum}, km (сумма lmh) = ${d.km_sum}\n`;
          if (d.filter_note) text += `\n(${d.filter_note})\n`;
          text += '\n';
          if (d.header_truncated) {
            text += `Внимание: список карточек в сообщении обрезан (лимит заголовка). Показано: ${(d.cards || []).length}`;
            if (d.cards_omitted != null) text += `, не вошло в сообщение: ${d.cards_omitted}`;
            text += '.\n\n';
          }
          if (d.message) {
            text += `${d.message}\n`;
          } else if (Array.isArray(d.cards) && d.cards.length) {
            d.cards.forEach((c, i) => {
              text += `${i + 1}) id: ${c.id}\n`;
              text += `   дата в журнале (изм./созд.): ${c.journal_date || '—'}\n`;
              if (c.date_modified || c.date_submitted) {
                text += `   date_modified: ${c.date_modified || '—'}, date_submitted: ${c.date_submitted || '—'}\n`;
              }
              if (c.document_name) text += `   документ: ${c.document_name}\n`;
              text += `   form1 найден: ${c.form1_found ? 'да' : 'нет'}\n`;
              if (c.inner_keys && c.inner_keys.length) text += `   ключи после разбора JSON: ${c.inner_keys.join(', ')}\n`;
              if (c.form1_keys && c.form1_keys.length) text += `   ключи form1: ${c.form1_keys.join(', ')}\n`;
              text += `   slp (как прочитано): ${c.slp_raw} → в сумму lp: +${c.lp_add}\n`;
              text += `   lmh (как прочитано): ${c.lmh_raw} → в сумму km: +${c.km_add}\n`;
              if (!c.form1_found && c.shape_hint) text += `   подсказка по структуре data: ${c.shape_hint}\n`;
              text += '\n';
            });
          } else if (!d.message) {
            text += 'Карточек в выборке нет или список пуст.\n';
          }
          window.alert(text);
        } catch (e2) {
          console.warn('X-GLPM-Aggregation', e2, aggHdr);
        }
      }
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setGlpmMonthlyGenerating(false);
    }
  }, [
    selectedReportFormType,
    glpmMonthlyReportMonth,
    glpmEffectiveBranchName,
    glpmPickerFormData.srf,
    handbooks,
    prepareGeneratedReport,
  ]);

  const handleGlpmYearlyGenerateReport = React.useCallback(async () => {
    if (selectedReportFormType !== GLPM_YEARLY_PLANNED_VOLUMES_TYPE) return;
    // Филиал у годового отчёта не выбирается в форме — если он пуст, бэкенд
    // подставит филиал из профиля пользователя, поэтому branch_name его не блокирует.
    if (
      !glpmYearlyReportYear ||
      !String(glpmPickerFormData.srf || '').trim()
    ) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      window.alert('Необходима авторизация для формирования отчёта');
      return;
    }
    const srfId = String(glpmPickerFormData.srf || '').trim();
    const subjectsHandbook = handbookById(handbooks, 12);
    const subjectRef = subjectsHandbook?.references?.find((r) => String(r.id) === srfId);
    const regionPayload = (subjectRef?.name || '').trim() || srfId;

    setGlpmYearlyGenerating(true);
    try {
      const response = await fetch(
        `${GLPM_API_BASE}/reports/glpm-yearly-planned-volumes/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            region: regionPayload,
            year: glpmYearlyReportYear,
            date: getTodayReportDate(),
            branch_name: glpmEffectiveBranchName,
          }),
        }
      );
      if (!response.ok) {
        let msg = 'Не удалось сформировать отчёт';
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) msg = String(errJson.message);
        } catch {
          /* ignore */
        }
        window.alert(msg);
        return;
      }
      const fallbackName = `glpm_planned_volumes_${glpmYearlyReportYear}.xlsx`;
      await prepareGeneratedReport(response, GLPM_YEARLY_PLANNED_VOLUMES_TYPE, fallbackName, {
        kind: 'generated_file',
        title: getReportFormTypeLabel(GLPM_YEARLY_PLANNED_VOLUMES_TYPE),
      });
    } catch (e) {
      console.error(e);
      window.alert('Ошибка сети при формировании отчёта');
    } finally {
      setGlpmYearlyGenerating(false);
    }
  }, [
    selectedReportFormType,
    glpmYearlyReportYear,
    glpmEffectiveBranchName,
    glpmPickerFormData.srf,
    handbooks,
    prepareGeneratedReport,
  ]);

  // Ключ для сохранения фильтров в localStorage
  const FILTERS_STORAGE_KEY = 'documents_filters';

  // Базовые значения фильтров по умолчанию
  const defaultFilters = {
    number: '',
    date: { min: '', max: '' }, // Диапазон дат от и до
    name: '',
    address: {
      subject: [],
      forestry: [],
      district_forestry: [],
      urochishche: [],
      kv: [],
      vid: [],
      slp: { min: '', max: '' }
    }, // Парсинг адреса по ", " - множественный выбор, slp - диапазон от и до
    tax_area: { min: '', max: '' }, // Диапазон от и до (S)
    dominant_species: [], // Массив для множественного выбора
    main_reason: [], // Массив для множественного выбора
    executor: [], // ФИО исполнителя — множественный выбор
    state: '',
    has_outbreak: '',
    user_approved: '',
    is_checked: ''
  };

  // Состояние для фильтров по столбцам, инициализируем из localStorage (если есть сохранённые значения)
  const [filters, setFilters] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultFilters;
    }
    try {
      const stored = window.localStorage.getItem(FILTERS_STORAGE_KEY);
      if (!stored) {
        return defaultFilters;
      }
      const parsed = JSON.parse(stored);
      // Аккуратно объединяем сохранённые значения с дефолтными, чтобы избежать пропусков полей
      return {
        ...defaultFilters,
        ...parsed,
        date: {
          ...defaultFilters.date,
          ...(parsed && parsed.date)
        },
        address: {
          ...defaultFilters.address,
          ...(parsed && parsed.address),
          slp: {
            ...defaultFilters.address.slp,
            ...(parsed && parsed.address && parsed.address.slp)
          }
        },
        tax_area: {
          ...defaultFilters.tax_area,
          ...(parsed && parsed.tax_area)
        }
      };
    } catch {
      return defaultFilters;
    }
  });

  // «Применённые» фильтры — на их основе делается серверный запрос и пересчёт опций.
  // Меню редактирует `filters` вживую (мгновенный отклик чекбоксов), а тяжёлая работа
  // (перезагрузка/фильтрация) запускается только при закрытии меню, когда мы копируем
  // `filters` -> `appliedFilters`. Это убирает лаги при выборе субъекта и т.п.
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Состояние для открытого меню фильтра
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState({ top: 0, left: 0 });

  // При закрытии меню фильтра применяем отредактированные фильтры.
  const prevOpenFilterMenuRef = useRef(null);
  useEffect(() => {
    const prev = prevOpenFilterMenuRef.current;
    prevOpenFilterMenuRef.current = openFilterMenu;
    if (prev && !openFilterMenu) {
      setAppliedFilters(filters);
    }
  }, [openFilterMenu, filters]);
  
  // Ref для контейнера таблицы
  const tableContainerRef = useRef(null);
  const reportsContentRef = useRef(null);
  
  // Состояние для поиска в списке причин
  const [reasonSearchText, setReasonSearchText] = useState('');
  
  // Состояние для поиска в списке пород
  const [speciesSearchText, setSpeciesSearchText] = useState('');
  
  // Поиск по частям адреса теперь живёт локально внутри компонента AddressFilterList,
  // чтобы ввод текста не перерисовывал всю страницу.

  // Состояние для черновика
  const [hasDraftData, setHasDraftData] = useState(false);

  // Модальное окно подтверждения удаления карточки
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteReport, setPendingDeleteReport] = useState(null); // { id, name }

  // Модальное окно подтверждения одобрения (проверки) карточки
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [pendingApproveReport, setPendingApproveReport] = useState(null); // { id, name }
  const [isApproving, setIsApproving] = useState(false);
  // Перечень незаполненных обязательных полей, из-за которых бэкенд отклонил
  // одобрение карточки: { name, errors: string[] }
  const [approveBlocked, setApproveBlocked] = useState(null);

  // Модальное окно подтверждения отмены одобрения («Вернуть»)
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [pendingRevertReport, setPendingRevertReport] = useState(null); // { id, name, kind: 'user'|'branch' }
  const [isReverting, setIsReverting] = useState(false);

  // Проверяем наличие черновика при загрузке и периодически обновляем
  useEffect(() => {
    const checkDraft = () => {
      setHasDraftData(hasDraft());
    };
    
    // Проверяем сразу
    checkDraft();
    
    // Проверяем каждые 5 секунд (на случай, если черновик был удален в другом месте)
    const interval = setInterval(checkDraft, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Загружаем отчёты из API с серверной пагинацией, фильтрацией и поиском.
  // Запрос пересобирается при изменении страницы, размера страницы, поиска и фильтров;
  // ввод/смена фильтров дебаунсятся, чтобы не дёргать сервер на каждый символ.
  useEffect(() => {
    if (!showJournalTable) {
      setIsLoading(false);
      setReports([]);
      setTotalRows(0);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    const loadReports = async () => {
      try {
        setIsLoading(true);
        const token = getAccessToken();
        if (!token) {
          setError('Необходима авторизация');
          setIsLoading(false);
          return;
        }

        // Колонки «ФИО исполнителя» у первого уровня нет и снять такой отбор ему
        // негде, а фильтры хранятся в браузере и могли остаться от другой учётной
        // записи — поэтому отбор по ФИО уходит только от тех, кто колонку видит.
        const requestFilters = roleSeesExecutor(user?.role)
          ? appliedFilters
          : { ...appliedFilters, executor: [] };
        const params = new URLSearchParams({
          page: String(currentPage),
          page_size: String(perPage),
          filters: JSON.stringify(requestFilters),
          type: 'VNN', // в формировании отчётов участвуют только карточки ВНН
        });
        if (searchText.trim()) params.set('search', searchText.trim());
        if (sortField) params.set('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField);

        const response = await fetch(`${GLPM_API_BASE}/reports/?${params}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });

        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          setReports(Array.isArray(data?.results) ? data.results : []);
          setTotalRows(typeof data?.count === 'number' ? data.count : 0);
          if (data?.options) setServerOptions(data.options);
          setError(null);
          // Сохранённая страница могла уехать за конец журнала (карточек стало
          // меньше или сузили фильтр) — тогда возвращаемся к первой, иначе
          // журнал выглядит пустым.
          const pages = typeof data?.num_pages === 'number' ? data.num_pages : 0;
          if (pages >= 1 && currentPage > pages) {
            setCurrentPage(1);
            setResetPaginationToggle((prev) => !prev);
          }
        } else if (response.status === 401) {
          setError('Ошибка авторизации');
        } else {
          setError('Ошибка загрузки данных');
        }
      } catch (err) {
        if (cancelled) return;
        setError('Ошибка подключения к серверу');
        console.error('Ошибка загрузки отчётов:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const timer = setTimeout(loadReports, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showJournalTable, currentPage, perPage, searchText, appliedFilters, sortField, sortDirection, reloadToken, user?.role]);

  // Сортировка задаётся кликом по заголовку колонки. Сортирует бэкенд (весь набор
  // после фильтрации), а не только текущая страница — иначе при переходе на
  // следующую страницу порядок «ломался» (виден только локальный сорт текущей страницы).
  const handleSort = React.useCallback((column, direction) => {
    const field = column.sortField;
    if (!field) return;
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    setResetPaginationToggle((prev) => !prev);
  }, []);

  // Парсинг даты проведения ВНН (хранится в формате ДД.ММ.ГГГГ, иногда ISO ГГГГ-ММ-ДД)
  // в объект Date для сравнения диапазонов.
  const parseVnnDate = (dateString) => {
    if (!dateString) return null;
    const s = String(dateString).trim();
    const ru = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (ru) {
      const d = new Date(Number(ru[3]), Number(ru[2]) - 1, Number(ru[1]));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Дата проведения ВНН хранится уже в формате ДД.ММ.ГГГГ — отдаём как есть
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(String(dateString).trim())) {
        return String(dateString).trim();
      }
      const date = new Date(dateString);
      // Отсекаем невалидную дату, чтобы не выводить "Invalid Date"
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('ru-RU');
    } catch {
      return '';
    }
  };

  // Определение состояния (санитарное/лесопатологическое)
  const getState = (report) => {
    return report.state || '';
  };

  // Определение наличия очага (получаем с бекенда)
  const hasOutbreak = (report) => {
    if (report.has_outbreak === null || report.has_outbreak === undefined) {
      return '-';
    }
    return report.has_outbreak ? 'Да' : 'Нет';
  };

  // Загрузка полных данных отчёта по ID
  const loadReportDetails = async (reportId) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error('Необходима авторизация');
      }

      const response = await fetch(`${GLPM_API_V2_BASE}/reports/${reportId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      if (response.ok) {
        const payload = await response.json();
        // API v2: { document: { properties: {...}, content: {...} } }
        return payload?.document || null;
      } else if (response.status === 401) {
        throw new Error('Ошибка авторизации');
      } else {
        throw new Error('Ошибка загрузки данных отчёта');
      }
    } catch (err) {
      console.error('Ошибка загрузки отчёта:', err);
      throw err;
    }
  };

  // Открыть модальное окно подтверждения удаления
  const handleDeleteReport = (e, row) => {
    e.stopPropagation();
    if (isAdminLevel4) return;
    setPendingDeleteReport({ id: row.id, name: row.name || 'карточка' });
    setShowDeleteConfirm(true);
  };

  // Закрыть модальное окно без удаления
  const cancelDeleteReport = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteReport(null);
  };

  // Выполнить удаление отчёта (после подтверждения в модальном окне)
  const performDeleteReport = async () => {
    if (!pendingDeleteReport) return;
    if (isAdminLevel4) {
      setShowDeleteConfirm(false);
      setPendingDeleteReport(null);
      return;
    }
    const reportId = pendingDeleteReport.id;
    try {
      const token = getAccessToken();
      if (!token) {
        setError('Необходима авторизация');
        setShowDeleteConfirm(false);
        setPendingDeleteReport(null);
        return;
      }
      const response = await fetch(`${GLPM_API_V2_BASE}/reports/${reportId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        // Перезагружаем журнал с сервера вместо локального filter(): так пагинация
        // (totalRows, число страниц) и состав текущей страницы остаются корректными,
        // даже если удалённая карточка была последней на странице.
        setReloadToken(t => t + 1);
        setShowDeleteConfirm(false);
        setPendingDeleteReport(null);
      } else if (response.status === 401) {
        setError('Ошибка авторизации');
        setShowDeleteConfirm(false);
        setPendingDeleteReport(null);
      } else if (response.status === 403 || response.status === 404) {
        // Сервер называет причину отказа (нет права, карточка согласована и т. п.)
        const payload = await response.json().catch(() => ({}));
        setShowDeleteConfirm(false);
        setPendingDeleteReport(null);
        alert(payload?.message || 'Не удалось удалить карточку.');
      } else {
        const text = await response.text();
        console.error('Ошибка удаления отчёта:', response.status, text);
        alert('Не удалось удалить карточку. Попробуйте позже.');
      }
    } catch (err) {
      console.error('Ошибка при удалении отчёта:', err);
      alert('Ошибка подключения к серверу.');
    }
  };

  const isReportOwner = React.useCallback((row) => {
    if (!row || user?.id === undefined || user?.id === null) return false;
    return String(row.ownerId ?? row.user_id ?? row.user?.id ?? '') === String(user.id);
  }, [user?.id]);

  // Открыть модальное окно подтверждения одобрения карточки.
  // kind: 'user' — первое одобрение (ставит владелец); 'branch' — второе
  // одобрение (ставит филиал, LEVEL2/LEVEL3). Проверку обязательных полей выполняет
  // бэкенд при одобрении пользователем: если поля не заполнены — вернёт 422 с перечнем.
  const handleApproveReport = (e, row, kindOverride = null) => {
    e.stopPropagation();
    const kind = kindOverride || (!row.user_approved && isReportOwner(row) ? 'user' : 'branch');
    setPendingApproveReport({ id: row.id, name: row.name || 'карточка', kind });
    setShowApproveConfirm(true);
  };

  // Закрыть модальное окно без одобрения
  const cancelApproveReport = () => {
    if (isApproving) return;
    setShowApproveConfirm(false);
    setPendingApproveReport(null);
  };

  // Одобрить сразу все выбранные карточки (POST /v2/approval/bulk/?action=approve).
  // Сервер проверяет каждую так же, как при одобрении по одной, и возвращает итог:
  // показываем, сколько одобрено и почему не одобрены остальные.
  const performBulkApprove = async () => {
    const rows = selectedForApproval;
    if (!rows.length || isBulkApproving) return;
    if (!window.confirm(`Одобрить выбранные карточки (${rows.length})?`)) return;
    setIsBulkApproving(true);
    try {
      const response = await fetch(`${GLPM_API_V2_BASE}/approval/bulk/?action=approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: rows.map((row) => row.id) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 401) {
        setError('Ошибка авторизации');
        return;
      }
      if (!response.ok) {
        alert(payload?.message || 'Не удалось одобрить карточки.');
        return;
      }
      const names = new Map(rows.map((row) => [String(row.id), row.name || 'карточка']));
      const failed = (payload.results || []).filter((item) => !item.ok);
      let text = `Одобрено карточек: ${payload.approved ?? 0}.`;
      if (failed.length) {
        const lines = failed.slice(0, 15).map((item) => {
          // Незаполненных полей бывает два десятка: в окне — первые три, полный список
          // виден при одобрении карточки по одной
          const errors = item.errors || [];
          const shown = errors.slice(0, 3).join('; ') + (errors.length > 3 ? ` и ещё ${errors.length - 3}` : '');
          const why = errors.length ? `${item.message} ${shown}` : item.message;
          return `— ${names.get(String(item.id)) || item.id}: ${why}`;
        });
        if (failed.length > 15) lines.push(`… и ещё ${failed.length - 15}`);
        text += `\nНе одобрено: ${failed.length}.\n${lines.join('\n')}`;
      }
      alert(text);
      setSelectedForApproval([]);
      setReloadToken((t) => t + 1);
    } catch (err) {
      console.error('Ошибка массового одобрения:', err);
      alert('Ошибка подключения к серверу.');
    } finally {
      setIsBulkApproving(false);
    }
  };

  // Выполнить одобрение карточки (после подтверждения в модальном окне).
  // API v2: единый POST /v2/approval/{id}/?action=... вместо approve-user/approve.
  // kind='user' -> submit (отправить на согласование), kind='branch'/'both' -> approve
  // (approve на бэкенде сам делает submit+approve одним вызовом, если инициатор — владелец).
  const performApproveReport = async () => {
    if (!pendingApproveReport) return;
    const reportId = pendingApproveReport.id;
    const kind = pendingApproveReport.kind === 'user' ? 'user' : (pendingApproveReport.kind === 'both' ? 'both' : 'branch');
    const action = kind === 'user' ? 'submit' : 'approve';
    try {
      setIsApproving(true);
      const token = getAccessToken();
      if (!token) {
        setError('Необходима авторизация');
        setShowApproveConfirm(false);
        setPendingApproveReport(null);
        return;
      }
      const response = await fetch(`${GLPM_API_V2_BASE}/approval/${reportId}/?action=${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        // Перезагружаем журнал с сервера — карточка могла измениться в полях,
        // зависящих от статуса (сортировка/фильтры), локальный патч это не отражал.
        setReloadToken(t => t + 1);
        setShowApproveConfirm(false);
        setPendingApproveReport(null);
      } else if (response.status === 401) {
        setError('Ошибка авторизации');
        setShowApproveConfirm(false);
        setPendingApproveReport(null);
      } else if (response.status === 403 || response.status === 409) {
        const payload = await response.json().catch(() => ({}));
        setShowApproveConfirm(false);
        setPendingApproveReport(null);
        alert(payload?.message || (kind === 'user'
          ? 'Отправить карточку на согласование может только владелец.'
          : 'Согласовывать карточки может только пользователь уровня 2 или 3.'));
      } else if (response.status === 422) {
        // Бэкенд отклонил одобрение из-за незаполненных обязательных полей.
        // Закрываем подтверждение и показываем перечень полей.
        const cardName = pendingApproveReport.name;
        let errors = [];
        try {
          const payload = await response.json();
          if (Array.isArray(payload?.errors)) {
            errors = payload.errors;
          }
        } catch (e) {
          errors = [];
        }
        setShowApproveConfirm(false);
        setPendingApproveReport(null);
        setApproveBlocked({ name: cardName, errors });
      } else {
        const text = await response.text();
        console.error('Ошибка одобрения карточки:', response.status, text);
        alert('Не удалось одобрить карточку. Попробуйте позже.');
      }
    } catch (err) {
      console.error('Ошибка при одобрении карточки:', err);
      alert('Ошибка подключения к серверу.');
    } finally {
      setIsApproving(false);
    }
  };

  // Открыть модальное окно подтверждения отмены одобрения.
  // kind: 'user' — снять одобрение пользователя; 'branch' — снять одобрение филиала.
  // Для kind='user' запоминаем, свою ли карточку возвращает пользователь: владелец
  // отзывает (withdraw), а филиал/LEVEL3 на чужой карточке — отклоняют (reject).
  const handleRevertReport = (e, row, kind) => {
    e.stopPropagation();
    setPendingRevertReport({ id: row.id, name: row.name || 'карточка', kind, isOwner: isReportOwner(row) });
    setShowRevertConfirm(true);
  };

  // Закрыть модальное окно без отмены одобрения
  const cancelRevertReport = () => {
    if (isReverting) return;
    setShowRevertConfirm(false);
    setPendingRevertReport(null);
  };

  // Выполнить отмену одобрения (после подтверждения в модальном окне).
  // API v2: единый POST /v2/approval/{id}/?action=... вместо unapprove/unapprove-user.
  // kind='user' -> withdraw, если это свой документ (PENDING -> NONE), иначе reject
  // (PENDING -> REJECTED, чужой документ может вернуть только филиал/LEVEL3).
  // kind='branch' -> unapprove (APPROVED -> PENDING).
  const performRevertReport = async () => {
    if (!pendingRevertReport) return;
    const reportId = pendingRevertReport.id;
    const kind = pendingRevertReport.kind === 'user' ? 'user' : 'branch';
    const action = kind === 'user' ? (pendingRevertReport.isOwner ? 'withdraw' : 'reject') : 'unapprove';
    try {
      setIsReverting(true);
      const token = getAccessToken();
      if (!token) {
        setError('Необходима авторизация');
        setShowRevertConfirm(false);
        setPendingRevertReport(null);
        return;
      }
      const response = await fetch(`${GLPM_API_V2_BASE}/approval/${reportId}/?action=${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        // Перезагружаем журнал с сервера вместо локального патча: после «Вернуть»
        // карточка может вообще пропасть из выдачи для текущей роли (например, у
        // филиала — после отмены одобрения пользователем), локальный патч это не отражал.
        setReloadToken(t => t + 1);
        setShowRevertConfirm(false);
        setPendingRevertReport(null);
      } else if (response.status === 401) {
        setError('Ошибка авторизации');
        setShowRevertConfirm(false);
        setPendingRevertReport(null);
      } else if (response.status === 403 || response.status === 409) {
        const payload = await response.json().catch(() => ({}));
        setShowRevertConfirm(false);
        setPendingRevertReport(null);
        alert(payload?.message || (action === 'withdraw'
          ? 'Нельзя отозвать карточку, пока карточка одобрена филиалом. Сначала верните одобрение филиала.'
          : action === 'reject'
            ? 'Отклонить можно только карточку, отправленную на согласование.'
            : 'Карточка ещё не одобрена филиалом.'));
      } else {
        const text = await response.text();
        console.error('Ошибка отмены одобрения:', response.status, text);
        alert('Не удалось отменить одобрение. Попробуйте позже.');
      }
    } catch (err) {
      console.error('Ошибка при отмене одобрения:', err);
      alert('Ошибка подключения к серверу.');
    } finally {
      setIsReverting(false);
    }
  };

  // Обработчик клика на строку таблицы
  const handleRowClick = async (row) => {
    try {
      // Сохраняем id перед загрузкой данных
      const reportId = row.id;

      // Загружаем документ (API v2): { properties: {...}, content: {...} }
      const reportDocument = await loadReportDetails(reportId);
      const approvalStatus = reportDocument?.properties?.approvalState?.status || row.approvalStatus || 'NONE';

      // Формируем объект с id и data для передачи в onSelectDocument
      const docWithId = {
        id: reportId,  // Сохраняем id с сервера
        data: reportDocument?.content || {},  // Содержимое документа (form1/form2/...)
        name: row.name || reportDocument?.properties?.name || '',  // Название из списка или из properties
        version: reportDocument?.properties?.version,
        approvalStatus,  // Единый статус согласования (NONE/PENDING/APPROVED/REJECTED)
        // Разрешение на редактирование, посчитанное сервером (report_permissions.can_edit_report) —
        // единый источник правды вместо переизобретения роль×статус логики на клиенте.
        canEdit: reportDocument?.properties?.canEdit
      };

      // Передаём полные данные в onSelectDocument
      if (onSelectDocument) {
        onSelectDocument(docWithId);
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных отчёта:', error);
      // В случае ошибки передаём хотя бы базовые данные
      if (onSelectDocument) {
        onSelectDocument({ id: row.id, name: row.name, approvalStatus: row.approvalStatus || 'NONE' });
      }
    }
  };

  // Функция для обновления фильтра
  const handleFilterChange = (columnName, value) => {
    setFilters(prev => ({
      ...prev,
      [columnName]: value
    }));
  };

  // Сохраняем фильтры в localStorage при каждом их изменении
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Не удалось сохранить фильтры в localStorage', e);
    }
  }, [filters]);

  // Сохраняем выбранное число строк на странице, чтобы оно не слетало на 20
  // при повторном монтировании DocumentsPage (открытие карточки размонтирует журнал).
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(PER_PAGE_STORAGE_KEY, String(perPage));
    } catch (e) {
      console.error('Не удалось сохранить число строк на странице в localStorage', e);
    }
  }, [perPage]);

  // А вместе с ним и саму страницу — чтобы после проверки карточки вернуться
  // туда, где работали, а не в начало журнала.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(PAGE_STORAGE_KEY, String(currentPage));
    } catch (e) {
      console.error('Не удалось сохранить страницу журнала в localStorage', e);
    }
  }, [currentPage]);

  // Функция для обработки изменения выбора причины (множественный выбор)
  const handleReasonFilterChange = (reason, isChecked) => {
    setFilters(prev => {
      const currentReasons = prev.main_reason || [];
      if (isChecked) {
        // Добавляем причину, если её ещё нет
        if (!currentReasons.includes(reason)) {
          return {
            ...prev,
            main_reason: [...currentReasons, reason]
          };
        }
      } else {
        // Удаляем причину
        return {
          ...prev,
          main_reason: currentReasons.filter(r => r !== reason)
        };
      }
      return prev;
    });
  };

  // Функция для обработки изменения выбора породы (множественный выбор)
  const handleSpeciesFilterChange = (species, isChecked) => {
    setFilters(prev => {
      const currentSpecies = prev.dominant_species || [];
      if (isChecked) {
        // Добавляем породу, если её ещё нет
        if (!currentSpecies.includes(species)) {
          return {
            ...prev,
            dominant_species: [...currentSpecies, species]
          };
        }
      } else {
        // Удаляем породу
        return {
          ...prev,
          dominant_species: currentSpecies.filter(s => s !== species)
        };
      }
      return prev;
    });
  };

  // Стабильный обработчик для AddressFilterList: заменяет весь массив выбранных
  // значений одной части адреса. useCallback — чтобы React.memo списков не сбрасывался.
  const handleAddressPartChange = useCallback((partName, newValues) => {
    setFilters(prev => ({
      ...prev,
      address: { ...prev.address, [partName]: newValues }
    }));
  }, []);

  // Функция для открытия меню фильтра
  const handleOpenFilterMenu = (columnName, event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const menuWidth = 280;
    const menuHeight = 180;
    const paddingLeft = 10;
    const paddingRight = 15; // Отступ справа больше для лучшей видимости
    
    let top = rect.bottom + scrollY + 5;
    
    // Начальная позиция - слева от иконки (в координатах документа)
    let left = rect.left + scrollX;
    
    // Вычисляем правую границу меню в координатах viewport
    const viewportRight = window.innerWidth;
    const viewportLeft = 0;
    
    // Проверяем, помещается ли меню, если открыть его слева от иконки
    const rightEdgeInViewport = rect.left + menuWidth;
    const maxRightEdge = viewportRight - paddingRight; // Максимальная правая граница с отступом
    
    if (rightEdgeInViewport > maxRightEdge) {
      // Меню не помещается справа - открываем его слева от иконки
      // Выравниваем правый край меню с левым краем иконки
      left = rect.left + scrollX - menuWidth;
      
      // Если меню выходит за левый край viewport, выравниваем по левому краю
      const leftEdgeInViewport = left - scrollX;
      if (leftEdgeInViewport < viewportLeft + paddingLeft) {
        left = scrollX + paddingLeft;
      }
    }
    
    // Финальная проверка - гарантируем, что меню полностью в пределах viewport с отступами
    let finalLeftInViewport = left - scrollX;
    let finalRightInViewport = finalLeftInViewport + menuWidth;
    
    // Если выходит за правый край - сдвигаем влево с учетом отступа справа
    if (finalRightInViewport > maxRightEdge) {
      left = scrollX + viewportRight - menuWidth - paddingRight;
      finalLeftInViewport = left - scrollX;
      finalRightInViewport = finalLeftInViewport + menuWidth;
    }
    
    // Если выходит за левый край - сдвигаем вправо
    if (finalLeftInViewport < viewportLeft + paddingLeft) {
      left = scrollX + paddingLeft;
    }
    
    // Проверяем нижний край
    if (top + menuHeight > window.innerHeight + scrollY) {
      top = rect.top + scrollY - menuHeight;
    }
    
    // Проверяем верхний край
    if (top < scrollY + paddingLeft) {
      top = scrollY + paddingLeft;
    }
    
    setFilterMenuPosition({ top, left });
    const newOpenMenu = openFilterMenu === columnName ? null : columnName;
    setOpenFilterMenu(newOpenMenu);
    // Очищаем поиск при открытии/закрытии меню причины
    if (newOpenMenu !== 'main_reason') {
      setReasonSearchText('');
    }
  };

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterMenu && !event.target.closest('.filter-menu-container')) {
        setOpenFilterMenu(null);
        if (openFilterMenu === 'main_reason') {
          setReasonSearchText('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterMenu]);

  // Закрытие всех всплывающих меню по Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showDeleteConfirm) {
          event.preventDefault();
          cancelDeleteReport();
        } else if (openFilterMenu) {
          event.preventDefault();
          setOpenFilterMenu(null);
          if (openFilterMenu === 'main_reason') {
            setReasonSearchText('');
          }
          if (openFilterMenu === 'dominant_species') {
            setSpeciesSearchText('');
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openFilterMenu, showDeleteConfirm]);

  // Фильтрация отчётов по поисковому запросу и фильтрам столбцов
  const filteredReports = React.useMemo(() => {
    let filtered = reports;

    // Применяем фильтры по столбцам
    filtered = filtered.filter(report => {
      // Фильтр по номеру
      if (filters.number) {
        const reportNumber = report.number || '';
        if (!String(reportNumber).toLowerCase().includes(filters.number.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по дате (диапазон от и до)
      if (filters.date && (filters.date.min || filters.date.max)) {
        const reportDateStr = report.vnn_date;
        if (!reportDateStr) {
          return false;
        }
        
        // Преобразуем дату отчета в объект Date для сравнения
        const reportDate = parseVnnDate(reportDateStr);
        if (!reportDate) {
          return false;
        }
        
        // Преобразуем минимальную дату из формата YYYY-MM-DD в Date
        if (filters.date.min) {
          const minDate = new Date(filters.date.min);
          minDate.setHours(0, 0, 0, 0);
          if (reportDate < minDate) {
            return false;
          }
        }
        
        // Преобразуем максимальную дату из формата YYYY-MM-DD в Date
        if (filters.date.max) {
          const maxDate = new Date(filters.date.max);
          maxDate.setHours(23, 59, 59, 999);
          if (reportDate > maxDate) {
            return false;
          }
        }
      }

      // Фильтр по наименованию отчёта
      if (filters.name) {
        const reportName = report.name || '';
        if (!reportName.toLowerCase().includes(filters.name.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по адресу (парсинг по ", ") - множественный выбор
      // Формат: Субъект, Лесничество, Участковое лесничество, Квартал, Выдел (Лесопатологический выдел)
      if (filters.address && (filters.address.subject.length > 0 || filters.address.forestry.length > 0 || filters.address.district_forestry.length > 0 || filters.address.urochishche.length > 0 || filters.address.kv.length > 0 || filters.address.vid.length > 0 || filters.address.slp.min || filters.address.slp.max)) {
        const reportAddress = report.address || '';
        const addressParts = reportAddress.split(', ').map(part => part.trim());
        
        // Проверяем каждую часть адреса (множественный выбор)
        // parts[0] - Субъект
        if (filters.address.subject.length > 0) {
          if (!addressParts[0] || !filters.address.subject.includes(addressParts[0])) {
            return false;
          }
        }
        // parts[1] - Лесничество
        if (filters.address.forestry.length > 0) {
          if (!addressParts[1] || !filters.address.forestry.includes(addressParts[1])) {
            return false;
          }
        }
        // parts[2] - Участковое лесничество
        if (filters.address.district_forestry.length > 0) {
          if (!addressParts[2] || !filters.address.district_forestry.includes(addressParts[2])) {
            return false;
          }
        }
        // parts[3] - Квартал
        if (filters.address.kv.length > 0) {
          if (!addressParts[3] || !filters.address.kv.includes(addressParts[3])) {
            return false;
          }
        }
        // parts[4] - Выдел (может быть "6 (2)" или просто "6")
        if (filters.address.vid.length > 0) {
          const vidPart = addressParts[4] || '';
          // Извлекаем номер выдела (до скобки, если есть)
          const vidNumber = vidPart.split(' (')[0].trim();
          if (!vidNumber || !filters.address.vid.includes(vidNumber)) {
            return false;
          }
        }
        // Урочище и площадь больше не используются в новом формате, но оставляем для обратной совместимости
        if (filters.address.urochishche.length > 0) {
          // Урочище больше не в адресе, пропускаем
        }
        if (filters.address.slp.min || filters.address.slp.max) {
          // Площадь больше не в адресе, пропускаем
        }
      }

      // Фильтр по площади (S) - диапазон от и до
      if (filters.tax_area && (filters.tax_area.min || filters.tax_area.max)) {
        const reportArea = parseFloat(report.tax_area) || 0;
        const minArea = filters.tax_area.min ? parseFloat(filters.tax_area.min) : null;
        const maxArea = filters.tax_area.max ? parseFloat(filters.tax_area.max) : null;
        
        if (minArea !== null && reportArea < minArea) {
          return false;
        }
        if (maxArea !== null && reportArea > maxArea) {
          return false;
        }
      }

      // Фильтр по преобладающей породе (множественный выбор)
      if (filters.dominant_species && filters.dominant_species.length > 0) {
        const reportSpecies = report.dominant_species || '';
        if (!filters.dominant_species.includes(reportSpecies)) {
          return false;
        }
      }

      // Фильтр по причине (множественный выбор)
      if (filters.main_reason && filters.main_reason.length > 0) {
        const reportReason = (report.main_reason || '').toLowerCase();
        const matches = filters.main_reason.some(selectedReason => 
          reportReason.includes(selectedReason.toLowerCase())
        );
        if (!matches) {
          return false;
        }
      }

      // Фильтр по состоянию
      if (filters.state) {
        const reportState = getState(report);
        if (reportState !== filters.state) {
          return false;
        }
      }

      // Фильтр по очагу
      if (filters.has_outbreak) {
        const reportOutbreak = hasOutbreak(report);
        if (reportOutbreak !== filters.has_outbreak) {
          return false;
        }
      }

      // Фильтр по одобрению пользователя
      if (filters.user_approved) {
        const reportUserApproved = report.user_approved ? 'Да' : 'Нет';
        if (reportUserApproved !== filters.user_approved) {
          return false;
        }
      }

      // Фильтр по проверено
      if (filters.is_checked) {
        const reportChecked = report.is_checked ? 'Да' : 'Нет';
        if (reportChecked !== filters.is_checked) {
          return false;
        }
      }

      return true;
    });

    // Применяем общий поиск
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter(report => {
        const searchableFields = [
          report.name || '',
          report.address || '',
          report.tax_area || '',
          report.dominant_species || '',
          report.main_reason || '',
          report.state || '',
          hasOutbreak(report),
          report.is_checked ? 'Да' : 'Нет',
          formatDate(report.date_modified || report.date_submitted),
          report.number || ''
        ];

        return searchableFields.some(field =>
          String(field).toLowerCase().includes(searchLower)
        );
      });
    }

    // Серверная фильтрация и сортировка уже применены — возвращаем карточки
    // текущей страницы как есть (переменная filtered здесь не используется).
    void filtered;
    return reports;
  }, [reports]);

  // При изменении поиска или фильтров сбрасываем таблицу на первую страницу,
  // чтобы серверный запрос искал по всем карточкам с самого начала. На первом
  // рендере сбрасывать нечего: поиск и фильтры там не менялись, а страница
  // только что восстановлена из localStorage — иначе возврат из карточки
  // снова уводил бы в начало журнала.
  const searchAndFiltersTouchedRef = useRef(false);
  React.useEffect(() => {
    if (!searchAndFiltersTouchedRef.current) {
      searchAndFiltersTouchedRef.current = true;
      return;
    }
    setResetPaginationToggle((prev) => !prev);
    setCurrentPage(1);
  }, [searchText, appliedFilters]);

  // Вспомогательная функция для фильтрации данных по всем фильтрам кроме указанного
  const filterReportsExcluding = React.useCallback((excludeFilter, currentFilters, currentSearchText) => {
    let filtered = reports;

    // Применяем все фильтры кроме исключенного
    filtered = filtered.filter(report => {
      // Фильтр по номеру
      if (excludeFilter !== 'number' && currentFilters.number) {
        const reportNumber = report.number || '';
        if (!String(reportNumber).toLowerCase().includes(currentFilters.number.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по дате (диапазон от и до)
      if (excludeFilter !== 'date' && currentFilters.date && (currentFilters.date.min || currentFilters.date.max)) {
        const reportDateStr = report.vnn_date;
        if (!reportDateStr) {
          return false;
        }
        
        const reportDate = parseVnnDate(reportDateStr);
        if (!reportDate) {
          return false;
        }
        
        if (currentFilters.date.min) {
          const minDate = new Date(currentFilters.date.min);
          minDate.setHours(0, 0, 0, 0);
          if (reportDate < minDate) {
            return false;
          }
        }
        
        if (currentFilters.date.max) {
          const maxDate = new Date(currentFilters.date.max);
          maxDate.setHours(23, 59, 59, 999);
          if (reportDate > maxDate) {
            return false;
          }
        }
      }

      // Фильтр по наименованию
      if (excludeFilter !== 'name' && currentFilters.name) {
        const reportName = report.name || '';
        if (!reportName.toLowerCase().includes(currentFilters.name.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по адресу (парсинг по ", ") - множественный выбор
      if (excludeFilter !== 'address' && currentFilters.address && (currentFilters.address.subject.length > 0 || currentFilters.address.forestry.length > 0 || currentFilters.address.district_forestry.length > 0 || currentFilters.address.urochishche.length > 0 || currentFilters.address.kv.length > 0 || currentFilters.address.vid.length > 0 || currentFilters.address.slp.min || currentFilters.address.slp.max)) {
        const reportAddress = report.address || '';
        const addressParts = reportAddress.split(', ').map(part => part.trim());
        
        if (currentFilters.address.subject.length > 0) {
          if (!addressParts[0] || !currentFilters.address.subject.includes(addressParts[0])) {
            return false;
          }
        }
        if (currentFilters.address.forestry.length > 0) {
          if (!addressParts[1] || !currentFilters.address.forestry.includes(addressParts[1])) {
            return false;
          }
        }
        if (currentFilters.address.district_forestry.length > 0) {
          if (!addressParts[2] || !currentFilters.address.district_forestry.includes(addressParts[2])) {
            return false;
          }
        }
        // parts[3] - Квартал
        if (currentFilters.address.kv.length > 0) {
          if (!addressParts[3] || !currentFilters.address.kv.includes(addressParts[3])) {
            return false;
          }
        }
        // parts[4] - Выдел (может быть "6 (2)" или просто "6")
        if (currentFilters.address.vid.length > 0) {
          const vidPart = addressParts[4] || '';
          const vidNumber = vidPart.split(' (')[0].trim();
          if (!vidNumber || !currentFilters.address.vid.includes(vidNumber)) {
            return false;
          }
        }
        // Урочище и площадь больше не используются в новом формате
        if (currentFilters.address.urochishche.length > 0) {
          // Урочище больше не в адресе, пропускаем
        }
        if (currentFilters.address.slp.min || currentFilters.address.slp.max) {
          // Площадь больше не в адресе, пропускаем
        }
      }

      // Фильтр по площади (S) - диапазон от и до
      if (excludeFilter !== 'tax_area' && currentFilters.tax_area && (currentFilters.tax_area.min || currentFilters.tax_area.max)) {
        const reportArea = parseFloat(report.tax_area) || 0;
        const minArea = currentFilters.tax_area.min ? parseFloat(currentFilters.tax_area.min) : null;
        const maxArea = currentFilters.tax_area.max ? parseFloat(currentFilters.tax_area.max) : null;
        
        if (minArea !== null && reportArea < minArea) {
          return false;
        }
        if (maxArea !== null && reportArea > maxArea) {
          return false;
        }
      }

      // Фильтр по преобладающей породе (множественный выбор)
      if (excludeFilter !== 'dominant_species' && currentFilters.dominant_species && currentFilters.dominant_species.length > 0) {
        const reportSpecies = report.dominant_species || '';
        if (!currentFilters.dominant_species.includes(reportSpecies)) {
          return false;
        }
      }

      // Фильтр по причине (множественный выбор)
      if (excludeFilter !== 'main_reason' && currentFilters.main_reason && currentFilters.main_reason.length > 0) {
        const reportReason = (report.main_reason || '').toLowerCase();
        const matches = currentFilters.main_reason.some(selectedReason => 
          reportReason.includes(selectedReason.toLowerCase())
        );
        if (!matches) {
          return false;
        }
      }

      // Фильтр по состоянию
      if (excludeFilter !== 'state' && currentFilters.state) {
        const reportState = getState(report);
        if (reportState !== currentFilters.state) {
          return false;
        }
      }

      // Фильтр по очагу
      if (excludeFilter !== 'has_outbreak' && currentFilters.has_outbreak) {
        const reportOutbreak = hasOutbreak(report);
        if (reportOutbreak !== currentFilters.has_outbreak) {
          return false;
        }
      }

      // Фильтр по проверено
      if (excludeFilter !== 'is_checked' && currentFilters.is_checked) {
        const reportChecked = report.is_checked ? 'Да' : 'Нет';
        if (reportChecked !== currentFilters.is_checked) {
          return false;
        }
      }

      // Фильтр по одобрению пользователя
      if (excludeFilter !== 'user_approved' && currentFilters.user_approved) {
        const reportUserApproved = report.user_approved ? 'Да' : 'Нет';
        if (reportUserApproved !== currentFilters.user_approved) {
          return false;
        }
      }

      return true;
    });

    // Применяем общий поиск
    if (currentSearchText && currentSearchText.trim()) {
      const searchLower = currentSearchText.toLowerCase().trim();
      filtered = filtered.filter(report => {
        const searchableFields = [
          report.name || '',
          report.address || '',
          report.tax_area || '',
          report.dominant_species || '',
          report.main_reason || '',
          report.state || '',
          hasOutbreak(report),
          report.is_checked ? 'Да' : 'Нет',
          formatDate(report.date_modified || report.date_submitted),
          report.number || ''
        ];

        return searchableFields.some(field => 
          String(field).toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [reports]);

  // Списки значений для выпадающих фильтров приходят с сервера (по всему набору карточек)
  const uniqueReasons = React.useMemo(
    () => (Array.isArray(serverOptions.reasons) ? serverOptions.reasons : []),
    [serverOptions]
  );

  const uniqueSpecies = React.useMemo(
    () => (Array.isArray(serverOptions.species) ? serverOptions.species : []),
    [serverOptions]
  );

  const uniqueExecutors = React.useMemo(
    () => (Array.isArray(serverOptions.executors) ? serverOptions.executors : []),
    [serverOptions]
  );

  // Вспомогательная функция для фильтрации данных с учетом частичных фильтров адреса
  const filterReportsWithPartialAddress = React.useCallback((excludeAddressPart, currentFilters, currentSearchText) => {
    let filtered = reports;

    // Применяем все фильтры кроме адреса
    filtered = filtered.filter(report => {
      // Фильтр по номеру
      if (currentFilters.number) {
        const reportNumber = report.number || '';
        if (!String(reportNumber).toLowerCase().includes(currentFilters.number.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по дате (диапазон от и до)
      if (currentFilters.date && (currentFilters.date.min || currentFilters.date.max)) {
        const reportDateStr = report.vnn_date;
        if (!reportDateStr) {
          return false;
        }
        
        const reportDate = parseVnnDate(reportDateStr);
        if (!reportDate) {
          return false;
        }
        
        if (currentFilters.date.min) {
          const minDate = new Date(currentFilters.date.min);
          minDate.setHours(0, 0, 0, 0);
          if (reportDate < minDate) {
            return false;
          }
        }
        
        if (currentFilters.date.max) {
          const maxDate = new Date(currentFilters.date.max);
          maxDate.setHours(23, 59, 59, 999);
          if (reportDate > maxDate) {
            return false;
          }
        }
      }

      // Фильтр по наименованию
      if (currentFilters.name) {
        const reportName = report.name || '';
        if (!reportName.toLowerCase().includes(currentFilters.name.toLowerCase())) {
          return false;
        }
      }

      // Фильтр по площади (S) - диапазон от и до
      if (currentFilters.tax_area && (currentFilters.tax_area.min || currentFilters.tax_area.max)) {
        const reportArea = parseFloat(report.tax_area) || 0;
        const minArea = currentFilters.tax_area.min ? parseFloat(currentFilters.tax_area.min) : null;
        const maxArea = currentFilters.tax_area.max ? parseFloat(currentFilters.tax_area.max) : null;
        
        if (minArea !== null && reportArea < minArea) {
          return false;
        }
        if (maxArea !== null && reportArea > maxArea) {
          return false;
        }
      }

      // Фильтр по преобладающей породе (множественный выбор)
      if (currentFilters.dominant_species && currentFilters.dominant_species.length > 0) {
        const reportSpecies = report.dominant_species || '';
        if (!currentFilters.dominant_species.includes(reportSpecies)) {
          return false;
        }
      }

      // Фильтр по причине (множественный выбор)
      if (currentFilters.main_reason && currentFilters.main_reason.length > 0) {
        const reportReason = (report.main_reason || '').toLowerCase();
        const matches = currentFilters.main_reason.some(selectedReason => 
          reportReason.includes(selectedReason.toLowerCase())
        );
        if (!matches) {
          return false;
        }
      }

      // Фильтр по состоянию
      if (currentFilters.state) {
        const reportState = getState(report);
        if (reportState !== currentFilters.state) {
          return false;
        }
      }

      // Фильтр по очагу
      if (currentFilters.has_outbreak) {
        const reportOutbreak = hasOutbreak(report);
        if (reportOutbreak !== currentFilters.has_outbreak) {
          return false;
        }
      }

      // Фильтр по одобрению пользователя
      if (currentFilters.user_approved) {
        const reportUserApproved = report.user_approved ? 'Да' : 'Нет';
        if (reportUserApproved !== currentFilters.user_approved) {
          return false;
        }
      }

      // Фильтр по проверено
      if (currentFilters.is_checked) {
        const reportChecked = report.is_checked ? 'Да' : 'Нет';
        if (reportChecked !== currentFilters.is_checked) {
          return false;
        }
      }

      // Фильтр по адресу - применяем только те части, которые не исключены
      if (currentFilters.address) {
        const reportAddress = report.address || '';
        const addressParts = reportAddress.split(', ').map(part => part.trim());
        
        // Применяем фильтры по другим частям адреса (кроме исключенной)
        if (excludeAddressPart !== 'subject' && currentFilters.address.subject.length > 0) {
          if (!addressParts[0] || !currentFilters.address.subject.includes(addressParts[0])) {
            return false;
          }
        }
        if (excludeAddressPart !== 'forestry' && currentFilters.address.forestry.length > 0) {
          if (!addressParts[1] || !currentFilters.address.forestry.includes(addressParts[1])) {
            return false;
          }
        }
        if (excludeAddressPart !== 'district_forestry' && currentFilters.address.district_forestry.length > 0) {
          if (!addressParts[2] || !currentFilters.address.district_forestry.includes(addressParts[2])) {
            return false;
          }
        }
        // parts[3] - Квартал
        if (excludeAddressPart !== 'kv' && currentFilters.address.kv.length > 0) {
          if (!addressParts[3] || !currentFilters.address.kv.includes(addressParts[3])) {
            return false;
          }
        }
        // parts[4] - Выдел (может быть "6 (2)" или просто "6")
        if (excludeAddressPart !== 'vid' && currentFilters.address.vid.length > 0) {
          const vidPart = addressParts[4] || '';
          const vidNumber = vidPart.split(' (')[0].trim();
          if (!vidNumber || !currentFilters.address.vid.includes(vidNumber)) {
            return false;
          }
        }
        // Урочище и площадь больше не используются в новом формате
        if (excludeAddressPart !== 'urochishche' && currentFilters.address.urochishche.length > 0) {
          // Урочище больше не в адресе, пропускаем
        }
        if (excludeAddressPart !== 'slp' && (currentFilters.address.slp.min || currentFilters.address.slp.max)) {
          // Площадь больше не в адресе, пропускаем
        }
      }

      return true;
    });

    // Применяем общий поиск
    if (currentSearchText && currentSearchText.trim()) {
      const searchLower = currentSearchText.toLowerCase().trim();
      filtered = filtered.filter(report => {
        const searchableFields = [
          report.name || '',
          report.address || '',
          report.tax_area || '',
          report.dominant_species || '',
          report.main_reason || '',
          report.state || '',
          hasOutbreak(report),
          report.is_checked ? 'Да' : 'Нет',
          formatDate(report.date_modified || report.date_submitted),
          report.number || '',
          // Набор полей держим таким же, как в серверном поиске
          // (_report_passes_filters в views.py): иначе при поиске по ФИО
          // исполнителя выпадающие фильтры адреса окажутся пустыми.
          report.executor || ''
        ];

        return searchableFields.some(field =>
          String(field).toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [reports]);

  // Функция для умной фильтрации с приоритетом по началу строки
  const smartFilter = (items, searchText) => {
    if (!searchText) return items;
    const searchLower = searchText.toLowerCase();
    const filtered = items.filter(item => 
      item.toLowerCase().includes(searchLower)
    );
    // Сортируем: сначала те, что начинаются с поискового запроса, потом остальные
    return filtered.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const aStartsWith = aLower.startsWith(searchLower);
      const bStartsWith = bLower.startsWith(searchLower);
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // Если оба начинаются или оба не начинаются, сортируем по алфавиту
      return aLower.localeCompare(bLower);
    });
  };

  // Функция для числовой сортировки с поддержкой строк
  const numericSort = (a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);
    
    // Если оба числа, сортируем численно
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    
    // Если только одно число, числа идут первыми
    if (!isNaN(numA) && isNaN(numB)) return -1;
    if (isNaN(numA) && !isNaN(numB)) return 1;
    
    // Если оба строки, сортируем по алфавиту
    return String(a).localeCompare(String(b));
  };

  // Получаем все уникальные значения для каждой части адреса (без учета фильтров) - для быстрого поиска
  // Значения частей адреса для выпадающих фильтров берём с сервера (по всему набору карточек)
  const allUniqueAddressParts = React.useMemo(() => ({
    subjects: Array.isArray(serverOptions.subjects) ? serverOptions.subjects : [],
    forestries: Array.isArray(serverOptions.forestries) ? serverOptions.forestries : [],
    districtForestries: Array.isArray(serverOptions.district_forestries) ? serverOptions.district_forestries : [],
    urochishches: [],
    kvs: Array.isArray(serverOptions.kvs) ? serverOptions.kvs : [],
    vids: Array.isArray(serverOptions.vids) ? serverOptions.vids : [],
  }), [serverOptions]);

  // Получаем уникальные значения для каждой части адреса из отфильтрованных данных
  // Каждая часть учитывает фильтры по другим частям адреса
  const uniqueAddressParts = React.useMemo(() => {
    // Используем применённые фильтры, чтобы не пересчитывать на каждый клик в меню.
    const filters = appliedFilters;
    // Функция для проверки, состоит ли строка только из цифр
    const isOnlyDigits = (str) => /^\d+$/.test(str);

    // Уникальные субъекты (исключая фильтр по subject, но учитывая другие части адреса)
    const filteredForSubjects = filterReportsWithPartialAddress('subject', filters, searchText);
    const subjectSet = new Set();
    filteredForSubjects.forEach(report => {
      if (report.address) {
        const addressParts = report.address.split(', ').map(part => part.trim());
        if (addressParts[0] && !isOnlyDigits(addressParts[0])) {
          subjectSet.add(addressParts[0]);
        }
      }
    });

    // Уникальные лесничества (исключая фильтр по forestry, но учитывая другие части адреса)
    const filteredForForestries = filterReportsWithPartialAddress('forestry', filters, searchText);
    const forestrySet = new Set();
    filteredForForestries.forEach(report => {
      if (report.address) {
        const addressParts = report.address.split(', ').map(part => part.trim());
        if (addressParts[1] && !isOnlyDigits(addressParts[1])) {
          forestrySet.add(addressParts[1]);
        }
      }
    });

    // Уникальные участковые лесничества (исключая фильтр по district_forestry, но учитывая другие части адреса)
    const filteredForDistrictForestries = filterReportsWithPartialAddress('district_forestry', filters, searchText);
    const districtForestrySet = new Set();
    filteredForDistrictForestries.forEach(report => {
      if (report.address) {
        const addressParts = report.address.split(', ').map(part => part.trim());
        if (addressParts[2] && !isOnlyDigits(addressParts[2])) {
          districtForestrySet.add(addressParts[2]);
        }
      }
    });

    // Уникальные кварталы (исключая фильтр по kv, но учитывая другие части адреса)
    const filteredForKvs = filterReportsWithPartialAddress('kv', filters, searchText);
    const kvSet = new Set();
    filteredForKvs.forEach(report => {
      if (report.address) {
        const addressParts = report.address.split(', ').map(part => part.trim());
        // parts[3] - Квартал
        if (addressParts[3]) kvSet.add(addressParts[3]);
      }
    });

    // Уникальные выделы (исключая фильтр по vid, но учитывая другие части адреса)
    const filteredForVids = filterReportsWithPartialAddress('vid', filters, searchText);
    const vidSet = new Set();
    filteredForVids.forEach(report => {
      if (report.address) {
        const addressParts = report.address.split(', ').map(part => part.trim());
        // parts[4] - Выдел (может быть "6 (2)" или просто "6"), извлекаем номер выдела
        if (addressParts[4]) {
          const vidNumber = addressParts[4].split(' (')[0].trim();
          if (vidNumber) {
            vidSet.add(vidNumber);
          }
        }
      }
    });

    return {
      subjects: Array.from(subjectSet).sort(),
      forestries: Array.from(forestrySet).sort(),
      districtForestries: Array.from(districtForestrySet).sort(),
      urochishches: [], // Урочище больше не используется в новом формате
      kvs: Array.from(kvSet).sort(numericSort),
      vids: Array.from(vidSet).sort(numericSort)
    };
  }, [filterReportsWithPartialAddress, appliedFilters, searchText]);

  // Функция для создания заголовка с иконкой фильтра
  const createHeaderWithFilter = (columnName, displayName, align = 'left') => {
    // Иконка подсвечивается по «применённым» фильтрам, а не по черновику в меню —
    // так заголовок не пересчитывается на каждый клик по галочке.
    let hasFilter = false;
    if (columnName === 'main_reason' || columnName === 'dominant_species' || columnName === 'executor') {
      hasFilter = appliedFilters[columnName] && Array.isArray(appliedFilters[columnName]) && appliedFilters[columnName].length > 0;
    } else if (columnName === 'tax_area' || columnName === 'date') {
      hasFilter = appliedFilters[columnName] && (appliedFilters[columnName].min || appliedFilters[columnName].max);
    } else if (columnName === 'address') {
      const addressFilter = appliedFilters[columnName] || {};
      hasFilter =
        (addressFilter.subject && addressFilter.subject.length > 0) ||
        (addressFilter.forestry && addressFilter.forestry.length > 0) ||
        (addressFilter.district_forestry && addressFilter.district_forestry.length > 0) ||
        (addressFilter.kv && addressFilter.kv.length > 0) ||
        (addressFilter.vid && addressFilter.vid.length > 0);
    } else {
      hasFilter = appliedFilters[columnName] && appliedFilters[columnName] !== '';
    }
    return (
      <div 
        style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          justifyContent: align === 'center' ? 'center' : 'flex-start',
          width: '100%',
          minWidth: 0
        }}
      >
        <span style={{ textAlign: align === 'center' ? 'center' : 'left' }}>{displayName}</span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            const newOpenMenu = openFilterMenu === columnName ? null : columnName;

            if (columnName === 'address') {
              setFilterMenuPosition({
                top: window.innerHeight / 2,
                left: window.innerWidth / 2
              });
              setOpenFilterMenu(newOpenMenu);
              return;
            }

            const rect = e.currentTarget.getBoundingClientRect();
            // Используем ref контейнера таблицы
            const container = tableContainerRef.current;
            if (!container) return;
            
            const containerRect = container.getBoundingClientRect();
            
            // Вычисляем позицию относительно родительского контейнера
            let top = rect.bottom - containerRect.top + 5;
            let left = rect.left - containerRect.left;
            
            // Для фильтра по адресу используем большую ширину
            const menuWidth = columnName === 'address' ? 800 : 280;
            const menuHeight = columnName === 'address' ? 600 : 180;
            const paddingLeft = 10;
            const paddingRight = 15;
            
            // Проверяем, не выходит ли меню за правый край контейнера
            if (left + menuWidth > containerRect.width - paddingRight) {
              left = containerRect.width - menuWidth - paddingRight;
            }
            
            // Проверяем, не выходит ли меню за левый край контейнера
            if (left < paddingLeft) {
              left = paddingLeft;
            }
            
            // Проверяем, не выходит ли меню за нижний край контейнера
            if (top + menuHeight > containerRect.height) {
              top = rect.top - containerRect.top - menuHeight;
            }
            
            // Проверяем, не выходит ли меню за верхний край контейнера
            if (top < paddingLeft) {
              top = paddingLeft;
            }
            
            setFilterMenuPosition({ top, left });
            setOpenFilterMenu(newOpenMenu);
          }}
          style={{
            cursor: 'pointer',
            color: hasFilter ? '#007bff' : '#999',
            userSelect: 'none',
            padding: '2px 4px',
            borderRadius: '3px',
            transition: 'background-color 0.2s',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          title="Фильтр"
          aria-label={`Фильтр: ${displayName}`}
        >
          <Icon24Search width={16} height={16} />
        </span>
      </div>
    );
  };

  // Функция для форматирования адреса при отображении (убирает части "-")
  const formatAddressForDisplay = (address) => {
    if (!address) return '-';
    // Разбиваем адрес по запятым и убираем части, которые равны "-"
    const parts = address.split(', ').map(part => part.trim()).filter(part => part !== '-');
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  // LEVEL2/LEVEL3/LEVEL4 видят карточки других пользователей — показываем ФИО исполнителя
  const showsExecutorColumn = roleSeesExecutor(user?.role);
  // Одобрять (проверять) карточки и удалять проверенные могут только LEVEL2 и LEVEL3
  const canApprove = user?.role === 'LEVEL2' || user?.role === 'LEVEL3';
  // LEVEL4 — администратор: видит журнал всех карточек ВНН и список пользователей
  const isAdminLevel4 = user?.role === 'LEVEL4';
  // Первое одобрение (одобрение пользователя) ставит сам владелец — это LEVEL1.
  const isLevel1 = user?.role === 'LEVEL1';
  // LEVEL3 — главный администратор: столбец действий разбивается на «Действия пользователя»
  // и «Действия филиала» (см. actions-колонки ниже).
  const isLevel3 = user?.role === 'LEVEL3';
  // Два столбца одобрения (пользователь + филиал) показываем LEVEL3 и LEVEL4.
  const showTwoApprovalColumns = user?.role === 'LEVEL3' || user?.role === 'LEVEL4';
  // Доступ к журналу карточек открывается при наличии права MANAGE_OWN_CARDS
  // (новый пользователь регистрируется без прав и ждёт одобрения администратора),
  // а также администратору LEVEL4 — он видит все карточки ВНН.
  const hasCardsAccess =
    isAdminLevel4 ||
    (Array.isArray(user?.permissions) && user.permissions.includes('MANAGE_OWN_CARDS'));

  // Определение колонок для таблицы.
  // Мемоизируем: пересобираем только при смене применённых фильтров, открытого меню
  // или роли пользователя — а не на каждый клик по галочке в меню фильтра.
  const selectedIds = React.useMemo(
    () => new Set(selectedForApproval.map((row) => String(row.id))),
    [selectedForApproval],
  );
  const isPendingRow = (row) => (row.approvalStatus || 'NONE') === 'PENDING';
  const toggleApprovalSelection = (row) => {
    setSelectedForApproval((prev) => (prev.some((r) => String(r.id) === String(row.id))
      ? prev.filter((r) => String(r.id) !== String(row.id))
      : [...prev, row]));
  };

  const columns = React.useMemo(() => [
    ...(showsExecutorColumn ? [{
      name: createHeaderWithFilter('executor', 'ФИО исполнителя', 'center'),
      selector: row => row.executor || '-',
      sortable: true,
      sortField: 'executor',
      minWidth: '160px',
      wrap: true,
      center: true,
    }] : []),
    {
      name: createHeaderWithFilter('number', '№', 'center'),
      selector: (row, index) => {
        // Формируем номер: number с бэкенда + постоянный номер карточки (seq).
        // seq закреплён за карточкой навсегда и не зависит от страницы/сортировки.
        // Фолбэк на позиционный индекс — только если seq по какой-то причине отсутствует.
        const number = row.number || '';
        const cardNumber = (row.seq !== null && row.seq !== undefined)
          ? row.seq.toString().padStart(4, '0')
          : (index + 1).toString().padStart(4, '0');

        if (number) {
          // Если есть number, добавляем к нему номер карточки
          return `${number}.${cardNumber}`;
        }
        // Если нет number, показываем только номер карточки
        return cardNumber;
      },
      sortable: false,
      width: '200px',
      center: true,
      style: {
        fontWeight: '500'
      }
    },
    {
      name: 'Дата',
      selector: row => {
        const date = formatDate(row.vnn_date);
        return date || '-';
      },
      sortable: true,
      sortField: 'vnn_date',
      width: '120px',
      center: true,
      name: createHeaderWithFilter('date', 'Дата', 'center')
    },
    {
      name: 'Наименование отчёта',
      selector: row => row.name || '-',
      sortable: true,
      sortField: 'name',
      minWidth: '160px',
      grow: 1,
      wrap: true,
      name: createHeaderWithFilter('name', 'Наименование отчёта', 'center')
    },
    {
      name: 'Адрес',
      selector: row => formatAddressForDisplay(row.address),
      sortable: true,
      sortField: 'address',
      minWidth: '200px',
      grow: 2,
      wrap: true,
      name: createHeaderWithFilter('address', 'Адрес', 'center')
    },
    {
      name: 'S',
      selector: row => row.tax_area || '-',
      sortable: true,
      sortField: 'tax_area',
      width: '100px',
      center: true,
      name: createHeaderWithFilter('tax_area', 'S', 'center')
    },
    {
      name: 'Преобл. порода',
      selector: row => row.dominant_species || '-',
      sortable: true,
      sortField: 'dominant_species',
      width: '130px',
      center: true,
      wrap: true,
      name: createHeaderWithFilter('dominant_species', 'Преобл. порода', 'center')
    },
    {
      name: 'Причина (главная)',
      selector: row => row.main_reason || '-',
      sortable: true,
      sortField: 'main_reason',
      width: '140px',
      center: true,
      wrap: true,
      name: createHeaderWithFilter('main_reason', 'Причина (главная)', 'center')
    },
    {
      name: 'Состояние',
      sortable: true,
      sortField: 'state',
      width: '180px',
      center: true,
      name: createHeaderWithFilter('state', 'Состояние', 'center'),
      cell: row => {
        const state = getState(row) || '-';
        return (
        <div style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
            <div style={{ fontWeight: '500' }}>{state}</div>
        </div>
        );
      },
      selector: row => getState(row) || '-'
    },
    // Столбцы одобрения:
    //  - LEVEL3/LEVEL4 — два столбца (одобрение пользователя + одобрение филиала);
    //  - LEVEL1 — только одобрение пользователя;
    //  - LEVEL2 — только одобрение филиала.
    ...(() => {
      const cellFor = (key) => (row) => (
        <span style={{ fontWeight: row[key] ? '600' : '400', color: '#000' }}>
          {row[key] ? 'Да' : 'Нет'}
        </span>
      );
      const userApprovalColumn = {
        name: createHeaderWithFilter('user_approved', 'Одобрение пользователя', 'center'),
        selector: row => row.user_approved ? 'Да' : 'Нет',
        sortable: true,
        sortField: 'user_approved',
        width: '170px',
        center: true,
        cell: cellFor('user_approved')
      };
      const branchApprovalColumn = {
        name: 'Одобрение филиала',
        selector: row => row.is_checked ? 'Да' : 'Нет',
        sortable: true,
        sortField: 'is_checked',
        width: '170px',
        center: true,
        name: createHeaderWithFilter('is_checked', 'Одобрение филиала', 'center'),
        cell: cellFor('is_checked')
      };
      if (showTwoApprovalColumns) return [userApprovalColumn, branchApprovalColumn];
      if (isLevel1) return [userApprovalColumn];
      return [branchApprovalColumn];
    })(),
    ...(!isAdminLevel4 ? (
      isLevel3 ? [
        {
          // Действия, доступные на этапе одобрения пользователем: удалить карточку,
          // пока она не одобрена, и одобрить её от имени пользователя — LEVEL3 может
          // делать это для любой карточки, не только своей (бэкенд это тоже разрешает).
          name: 'Действия пользователя',
          width: '170px',
          center: true,
          sortable: false,
          cell: row => {
            // Через approvalStatus напрямую (а не через устаревшие is_checked/user_approved,
            // которые не различают PENDING/APPROVED/REJECTED), чтобы корректно учитывать
            // REJECTED — после отклонения карточка возвращается «в работу к пользователю»
            // и должна снова допускать отправку на согласование (submit).
            const status = row.approvalStatus || 'NONE';
            const canDelete = status === 'NONE' || status === 'REJECTED';
            const canUserApprove = status === 'NONE' || status === 'REJECTED';
            // Доступно только пока карточка на согласовании. На своей карточке (редкий
            // случай) это withdraw, на чужой — reject (LEVEL3 отклоняет за пользователя).
            const canRevertUser = status === 'PENDING';
            const revertUserIsOwn = isReportOwner(row);
            return (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => handleDeleteReport(e, row)}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Удалить карточку"
                  >
                    Удалить
                  </button>
                )}
                {canUserApprove && (
                  <button
                    type="button"
                    onClick={(e) => handleApproveReport(e, row, 'user')}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Одобрить от имени пользователя"
                  >
                    Одобрить
                  </button>
                )}
                {canRevertUser && (
                  <button
                    type="button"
                    onClick={(e) => handleRevertReport(e, row, 'user')}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={revertUserIsOwn ? "Отозвать свою карточку из согласования" : "Отклонить карточку, вернуть на доработку"}
                  >
                    {revertUserIsOwn ? "Отменить отправку" : "Отклонить"}
                  </button>
                )}
              </div>
            );
          }
        },
        {
          // Действия филиала: удалить карточку (доступно и для уже одобренных), одобрить
          // от имени филиала (кнопка появляется только после одобрения пользователем)
          // и вернуть (снять) уже поставленное одобрение филиала.
          name: 'Действия филиала',
          width: '170px',
          center: true,
          sortable: false,
          cell: row => {
            const status = row.approvalStatus || 'NONE';
            const canDelete = status === 'PENDING' || status === 'APPROVED';
            const canBranchApprove = status === 'PENDING';
            const canRevertBranch = status === 'APPROVED';
            return (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => handleDeleteReport(e, row)}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Удалить карточку"
                  >
                    Удалить
                  </button>
                )}
                {canBranchApprove && (
                  <button
                    type="button"
                    onClick={(e) => handleApproveReport(e, row, 'branch')}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Одобрить от имени филиала"
                  >
                    Одобрить
                  </button>
                )}
                {canRevertBranch && (
                  <button
                    type="button"
                    onClick={(e) => handleRevertReport(e, row, 'branch')}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#6c757d',
                      backgroundColor: 'transparent',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Отменить одобрение филиала"
                  >
                    Отменить одобрение
                  </button>
                )}
              </div>
            );
          }
        }
      ] : [{
        name: 'Действия',
        width: (canApprove || isLevel1) ? '200px' : '90px',
        center: true,
        sortable: false,
        cell: row => {
          const status = row.approvalStatus || 'NONE';
          // Остальные могут удалять свои непроверенные карточки; LEVEL2 также согласованные.
          const canDelete = status !== 'APPROVED' || canApprove;
          // «Оба сразу» (submit+approve одним кликом) — только из NONE (бэкенд делает
          // это только из NONE, не из REJECTED — для REJECTED нужен отдельный submit).
          const canApproveOwnAsBranch = canApprove && status === 'NONE' && isReportOwner(row);
          // Отправить на согласование можно из NONE и из REJECTED (после отклонения).
          const canUserApprove = !canApproveOwnAsBranch && (status === 'NONE' || status === 'REJECTED') && isReportOwner(row);
          const canBranchApprove = canApprove && status === 'PENDING';
          // «Отменить одобрение» (снять согласование филиала) — только для филиала (LEVEL2)
          // и только пока карточка действительно согласована.
          const canRevertBranch = canApprove && status === 'APPROVED';
          // «Вернуть» (снять отправку на согласование, вернуть карточку владельцу) —
          // только для филиала (LEVEL2), только пока карточка ожидает согласования.
          // На чужой карточке (обычный случай) это reject, на своей (редкий случай,
          // когда LEVEL2 сам владелец) — withdraw; см. isOwner в handleRevertReport.
          const canReturnToUser = canApprove && status === 'PENDING';
          // Отозвать свою же карточку из согласования — только владелец (LEVEL1),
          // пока никто ещё не принял решение.
          const canWithdrawOwn = isLevel1 && status === 'PENDING' && isReportOwner(row);
          const approveKind = canApproveOwnAsBranch ? 'both' : (canUserApprove ? 'user' : 'branch');
          return (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => handleDeleteReport(e, row)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Удалить карточку"
                >
                  Удалить
                </button>
              )}
              {(canApproveOwnAsBranch || canUserApprove || canBranchApprove) && (
                <button
                  type="button"
                  onClick={(e) => handleApproveReport(e, row, approveKind)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Одобрить карточку"
                >
                  Одобрить
                </button>
              )}
              {canRevertBranch && (
                <button
                  type="button"
                  onClick={(e) => handleRevertReport(e, row, 'branch')}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Отменить одобрение филиала"
                >
                  Отменить одобрение
                </button>
              )}
              {canReturnToUser && (
                <button
                  type="button"
                  onClick={(e) => handleRevertReport(e, row, 'user')}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title={isReportOwner(row) ? "Отозвать свою карточку из согласования" : "Отклонить карточку, вернуть на доработку"}
                >
                  {isReportOwner(row) ? "Отозвать" : "Отклонить"}
                </button>
              )}
              {canWithdrawOwn && (
                <button
                  type="button"
                  onClick={(e) => handleRevertReport(e, row, 'user')}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Отозвать свою карточку из согласования"
                >
                  Отозвать
                </button>
              )}
            </div>
          );
        }
      }]
    ) : []),
    // Галочки для массового одобрения — последним столбцом, справа. Отметить можно
    // только карточки на согласовании; в шапке — отметить все такие на странице.
    ...(canApprove ? [{
      name: (() => {
        const pending = reports.filter(isPendingRow);
        const allChecked = pending.length > 0 && pending.every((row) => selectedIds.has(String(row.id)));
        return (
          <input
            type="checkbox"
            aria-label="Отметить все карточки на согласовании"
            title="Отметить все карточки на согласовании"
            checked={allChecked}
            disabled={pending.length === 0}
            onChange={() => setSelectedForApproval(allChecked ? [] : pending)}
            style={{ width: '16px', height: '16px', cursor: pending.length ? 'pointer' : 'default' }}
          />
        );
      })(),
      width: '56px',
      center: true,
      sortable: false,
      cell: (row) => {
        const pending = isPendingRow(row);
        return (
          <input
            type="checkbox"
            aria-label={`Отметить карточку ${row.name || ''}`.trim()}
            checked={selectedIds.has(String(row.id))}
            disabled={!pending}
            title={pending ? 'Отметить для одобрения' : 'Одобрить можно только карточку на согласовании'}
            // клик по галочке не должен открывать карточку
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleApprovalSelection(row)}
            style={{ width: '16px', height: '16px', cursor: pending ? 'pointer' : 'default' }}
          />
        );
      },
    }] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [showsExecutorColumn, canApprove, isAdminLevel4, isLevel1, isLevel3, showTwoApprovalColumns, isReportOwner, openFilterMenu, appliedFilters, reports, selectedIds]);

  // Кастомные стили для таблицы в цветах приложения (статичные — мемоизируем единожды)
  const customStyles = React.useMemo(() => ({
    headRow: {
      style: {
        backgroundColor: '#f5f5f5',
        color: '#000',
        minHeight: '52px',
        height: 'auto',
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#ddd',
        borderBottom: '1px solid #ddd',
      },
    },
    headCells: {
      style: {
        fontSize: '0.85rem',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '15px',
        paddingBottom: '15px',
        borderRight: '1px solid #ddd',
        color: '#000',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        lineHeight: '1.4',
        overflow: 'visible',
        height: 'auto',
        minHeight: '52px',
      },
    },
    cells: {
      style: {
        fontSize: '0.9rem',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '15px',
        paddingBottom: '15px',
        borderRight: '1px solid #ddd',
        borderBottom: '1px solid #ddd',
        color: '#000',
      },
    },
    rows: {
      style: {
        minHeight: '48px',
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
      },
      stripedStyle: {
        backgroundColor: '#f8f9fa',
      },
    },
    pagination: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#ddd',
        backgroundColor: '#f5f5f5',
      },
    },
  }), []);

  const topNavButtonStyle = (active = false) => ({
    height: '42px',
    paddingLeft: '20px',
    paddingRight: '20px',
    fontSize: '0.95rem',
    fontWeight: active ? '600' : '500',
    borderRadius: '6px',
    border: active ? '1px solid #6c757d' : '1px solid #ddd',
    backgroundColor: active ? '#6c757d' : '#f8f9fa',
    color: active ? '#fff' : '#495057',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  });

  const handleTopNavMouseEnter = (e, active = false) => {
    e.currentTarget.style.backgroundColor = active ? '#5a6268' : '#e8ecef';
    e.currentTarget.style.borderColor = active ? '#5a6268' : '#adb5bd';
  };

  const handleTopNavMouseLeave = (e, active = false) => {
    e.currentTarget.style.backgroundColor = active ? '#6c757d' : '#f8f9fa';
    e.currentTarget.style.borderColor = active ? '#6c757d' : '#ddd';
  };

  // Мемоизированный элемент таблицы. Таблица зависит только от данных с сервера
  // (которые меняются по «Применить»), но не от черновика фильтров в меню.
  // Благодаря этому клики по галочкам не перерисовывают тяжёлую таблицу.
  // Бледно-красная заливка строк-дубликатов (есть совпадающая карточка в организации)
  const dupConditionalRowStyles = React.useMemo(() => [
    {
      when: row => !!row.has_duplicate,
      style: {
        backgroundColor: 'rgba(220, 53, 69, 0.07)',
      },
    },
  ], []);

  // Выбор действует в пределах загруженной страницы: новая страница, фильтр или
  // перезагрузка журнала его сбрасывают
  useEffect(() => {
    setSelectedForApproval([]);
  }, [reports]);

  const journalTableElement = React.useMemo(() => (
    <DataTable
      columns={columns}
      data={filteredReports}
      customStyles={customStyles}
      conditionalRowStyles={dupConditionalRowStyles}
      onRowMouseEnter={(row, e) => {
        if (row && row.has_duplicate) {
          setDupTooltip({ x: e.clientX, y: e.clientY });
        }
      }}
      onRowMouseLeave={() => setDupTooltip(null)}
      highlightOnHover
      pointerOnHover
      striped
      persistTableHead
      noDataComponent={
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          {searchText ? 'Ничего не найдено' : 'Нет данных'}
        </div>
      }
      onRowClicked={handleRowClick}
      progressPending={isLoading}
      sortServer
      onSort={handleSort}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationDefaultPage={currentPage}
      paginationPerPage={perPage}
      paginationResetDefaultPage={resetPaginationToggle}
      onChangePage={(page) => setCurrentPage(page)}
      onChangeRowsPerPage={(newPerPage, page) => { setPerPage(newPerPage); setCurrentPage(page); }}
      paginationRowsPerPageOptions={[10, 20, 50, 100]}
      paginationComponentOptions={{
        rowsPerPageText: 'Строк на странице:',
        rangeSeparatorText: 'из',
        noRowsPerPage: false,
        selectAllRowsItem: false,
        selectAllRowsItemText: 'Все'
      }}
    />
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [columns, filteredReports, customStyles, dupConditionalRowStyles, searchText, isLoading, totalRows, currentPage, perPage, resetPaginationToggle, handleSort]);

  // Кнопка массового одобрения — как «Заполнить карточку ВНН», только чуть меньше
  const bulkApproveButton = (
    <Button
      size="s"
      mode="outline"
      appearance="neutral"
      disabled={isBulkApproving}
      style={{
        height: '34px',
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '0.85rem',
        fontWeight: '500',
        borderRadius: '6px',
        border: '1px solid #ddd',
        backgroundColor: '#f8f9fa',
        color: '#495057',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#e8ecef';
        e.currentTarget.style.borderColor = '#adb5bd';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#f8f9fa';
        e.currentTarget.style.borderColor = '#ddd';
      }}
      onClick={performBulkApprove}
    >
      {isBulkApproving ? 'Одобряем…' : 'Одобрить выбранные'}
    </Button>
  );


  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Заголовок */}
      <div className="top-menu mlr-0-8 bb-1 row space-between" style={{ 
        height: '5.5vh', 
        minHeight: '55px', 
        paddingTop: '8px', 
        paddingBottom: '8px',
        paddingLeft: '1.5vw',
        paddingRight: '1.5vw',
        alignItems: 'center'
      }}>
        {/* Логотип, подпись и кнопки слева */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          {/* Логотип */}
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', flexShrink: 0 }}>
            <img 
              src={logoImage} 
              alt="Рослес" 
              style={{ 
                height: '38px', 
                width: 'auto',
                objectFit: 'contain',
                maxHeight: '100%'
              }} 
            />
          </div>
          
          {/* Подпись ГЛПМ */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%',
            flexShrink: 0
          }}>
            <span style={{ 
              fontSize: '1rem',
              fontWeight: '600',
              color: '#495057',
              letterSpacing: '0.5px'
            }}>
              ГЛПМ
            </span>
          </div>
          
          {/* Кнопки слева */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '100%', marginLeft: '40px' }}>
            <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={topNavButtonStyle(location.pathname === '/')}
                onMouseEnter={(e) => handleTopNavMouseEnter(e, location.pathname === '/')}
                onMouseLeave={(e) => handleTopNavMouseLeave(e, location.pathname === '/')}
                onClick={() => navigate('/', { replace: false })}
              >
                Журнал карточек ВНН
            </Button>
            {isAdminLevel4 && (
            <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={topNavButtonStyle(location.pathname === '/users')}
                onMouseEnter={(e) => handleTopNavMouseEnter(e, location.pathname === '/users')}
                onMouseLeave={(e) => handleTopNavMouseLeave(e, location.pathname === '/users')}
                onClick={() => navigate('/users', { replace: false })}
              >
                Список пользователей
            </Button>
            )}
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={topNavButtonStyle(false)}
              onMouseEnter={(e) => handleTopNavMouseEnter(e, false)}
              onMouseLeave={(e) => handleTopNavMouseLeave(e, false)}
              onClick={() => {
                window.open(USER_GUIDE_URL, '_blank');
              }}
            >
              Руководство пользователя
            </Button>
            {canCreateReports && (
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={topNavButtonStyle(location.pathname === '/reports')}
              onMouseEnter={(e) => handleTopNavMouseEnter(e, location.pathname === '/reports')}
              onMouseLeave={(e) => handleTopNavMouseLeave(e, location.pathname === '/reports')}
              onClick={() => {
                navigate('/reports');
              }}
            >
              Отчёты
            </Button>
            )}
            {canManagePlans && (
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={topNavButtonStyle(location.pathname === '/plans')}
              onMouseEnter={(e) => handleTopNavMouseEnter(e, location.pathname === '/plans')}
              onMouseLeave={(e) => handleTopNavMouseLeave(e, location.pathname === '/plans')}
              onClick={() => {
                navigate('/plans');
              }}
            >
              Планы
            </Button>
            )}
          </div>
        </div>
        
        {/* Информация об аккаунте справа */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          {user && (
            <>
              <div style={{ 
                height: '32px', 
                display: 'flex', 
                alignItems: 'center',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderLeft: '1px solid #e0e0e0',
                marginLeft: '8px'
              }}>
                <span className="sz-0-8 b-5" style={{ 
                  color: '#495057', 
                  margin: 0, 
                  padding: 0, 
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap'
                }}>
                  {user.username}
                </span>
              </div>
              {onLogout && (
                <Button
                  size="m"
                  mode="outline"
                  appearance="neutral"
                  style={{ 
                    height: '42px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8ecef';
                    e.currentTarget.style.borderColor = '#adb5bd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                  onClick={onLogout}
                >
                  Выход
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {/* Основное содержимое - таблица */}
      {adminUsersMode ? (
      <UsersAdminPage embedded />
      ) : showJournalTable ? (
      !hasCardsAccess ? (
      <div ref={reportsContentRef} style={{ flex: 1, overflowY: 'auto', padding: '20px', paddingTop: '40px' }}>
        <div style={{ marginBottom: '20px' }}>
          <p className="sz-1-3 mt0 b-7" style={{ margin: 0, padding: 0, marginBottom: '48px' }}>{listHeading}</p>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          margin: '40px auto',
          maxWidth: '560px',
          padding: '32px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px', lineHeight: 1 }}>⏳</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#495057' }}>
            Недостаточно прав
          </div>
          <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: 1.5 }}>
            Ваша учётная запись зарегистрирована, но ещё не подтверждена.
            Необходимо дождаться одобрения администратора — после этого журнал карточек ВНН станет доступен.
          </div>
        </div>
      </div>
      ) : (
      <div ref={reportsContentRef} className="styled-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px', paddingTop: '40px' }}>
        {/* Заголовок над таблицей */}
        <div style={{ marginBottom: '20px' }}>
          <p className="sz-1-3 mt0 b-7" style={{ margin: 0, padding: 0, marginBottom: '48px' }}>{listHeading}</p>
          {/* Кнопка, уведомление о черновике и поле поиска на одной линии */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginLeft: '16px', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: '0 0 auto' }}>
              {/* Администратор LEVEL4 не создаёт карточки — только просмотр */}
              {!isAdminLevel4 && (
              <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={{
                  height: '40px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e8ecef';
                  e.currentTarget.style.borderColor = '#adb5bd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#ddd';
                }}
                onClick={() => {
                  // Обработчик для кнопки "Заполнить карточку ВНН"
                  if (onCreateDocument) {
                    onCreateDocument();
                  }
                }}
              >
                Заполнить карточку ВНН
              </Button>
              )}
              {/* Уведомление о черновике справа от кнопки */}
              {hasDraftData && (
                <div 
                  onClick={(e) => {
                    // Если клик не на иконку удаления, восстанавливаем черновик
                    if (!e.target.closest('.draft-delete-icon')) {
                      if (onRestoreDraft) {
                        onRestoreDraft();
                      }
                    }
                  }}
                  style={{
                    paddingLeft: '20px',
                    paddingRight: '12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '40px',
                    minWidth: '200px',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffe69c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff3cd';
                  }}
                >
                  <span style={{ fontSize: '0.9rem', color: '#856404' }}>
                    Есть черновик
                  </span>
                  <div
                    className="draft-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDraft();
                      setHasDraftData(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      marginLeft: '12px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon24Cancel fill="#856404" width={18} height={18} />
                  </div>
                </div>
              )}
            </div>
            {/* Поиск и кнопка очистки фильтров справа на одной линии */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginRight: '20px'
            }}>
              <div style={{ position: 'relative', width: '170px' }}>
                <input
                  type="text"
                  placeholder="Поиск"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applySearch();
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    fontSize: '0.9rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = '#e8ecef';
                    e.currentTarget.style.borderColor = '#adb5bd';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                />
              </div>
              <button
                type="button"
                onClick={applySearch}
                style={{
                  height: '40px',
                  padding: '0 18px',
                  borderRadius: '6px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6268';
                  e.currentTarget.style.borderColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                  e.currentTarget.style.borderColor = '#6c757d';
                }}
              >
                Поиск
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setSearchText('');
                  setFilters(defaultFilters);
                  setAppliedFilters(defaultFilters);
                }}
                style={{
                  height: '40px',
                  padding: '0 14px',
                  borderRadius: '6px',
                  border: '1px solid #ced4da',
                  backgroundColor: '#fff',
                  color: '#495057',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                Очистить фильтры
              </button>
            </div>
          </div>
        </div>
        {canApprove && (
          // Таблица у уровней 2 и 3 стоит чуть ниже: в этом промежутке, на фоне
          // страницы, справа появляется кнопка одобрения. Место занято всегда, поэтому
          // при первой галочке таблица не съезжает и клик не уходит в другую строку
          <div style={{ height: '46px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {selectedForApproval.length > 0 && bulkApproveButton}
          </div>
        )}
        {isLoading ? (
          <TreeLoader label="Загрузка журнала…" />
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
            {error}
          </div>
        ) : (
          <div 
            ref={tableContainerRef}
            style={{ 
            border: '1px solid #ddd', 
            borderRadius: '6px',
            overflow: 'visible',
              backgroundColor: 'white',
              position: 'relative'
            }}>
            {/* Overlay для затемнения фона */}
            {openFilterMenu && (
              <div
                onClick={(e) => {
                  // Закрываем меню при клике на overlay
                  if (e.target === e.currentTarget) {
                    setOpenFilterMenu(null);
                    if (openFilterMenu === 'main_reason') {
                      setReasonSearchText('');
                    }
                    if (openFilterMenu === 'dominant_species') {
                      setSpeciesSearchText('');
                    }
                  }
                }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  zIndex: 999,
                  transition: 'opacity 0.2s ease'
                }}
              />
            )}
            {/* Выпадающее меню фильтра */}
            {openFilterMenu && (
              <div 
                className="filter-menu-container"
                style={{
                  position: openFilterMenu === 'address' ? 'fixed' : 'absolute',
                  top: openFilterMenu === 'address' ? '50%' : `${filterMenuPosition.top}px`,
                  left: openFilterMenu === 'address' ? '50%' : `${filterMenuPosition.left}px`,
                  transform: openFilterMenu === 'address' ? 'translate(-50%, -50%)' : 'none',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  padding: '20px',
                  zIndex: 1000,
                  minWidth: openFilterMenu === 'address' ? undefined : '280px',
                  width: openFilterMenu === 'address' ? `${Math.min(900, window.innerWidth - 20)}px` : undefined,
                  maxWidth: openFilterMenu === 'address' ? `${Math.min(900, window.innerWidth - 20)}px` : `${Math.min(400, window.innerWidth - 20)}px`,
                  maxHeight: openFilterMenu === 'address' ? 'calc(100vh - 40px)' : undefined,
                  overflowY: openFilterMenu === 'address' ? 'auto' : undefined,
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              >
          <div style={{ 
                  marginBottom: '16px', 
                  fontWeight: '600', 
                  fontSize: '0.95rem',
                  color: '#495057'
                }}>
                  Фильтр: {
                    openFilterMenu === 'executor' ? 'ФИО исполнителя' :
                    openFilterMenu === 'number' ? '№' :
                    openFilterMenu === 'date' ? 'Дата' :
                    openFilterMenu === 'name' ? 'Наименование отчёта' :
                    openFilterMenu === 'address' ? 'Адрес' :
                    openFilterMenu === 'tax_area' ? 'S' :
                    openFilterMenu === 'dominant_species' ? 'Преобл. порода' :
                    openFilterMenu === 'main_reason' ? 'Причина (главная)' :
                    openFilterMenu === 'state' ? 'Состояние' :
                    openFilterMenu === 'has_outbreak' ? 'Очаг' :
                    openFilterMenu === 'user_approved' ? 'Одобрение пользователя' :
                    openFilterMenu === 'is_checked' ? 'Проверено' : ''
                  }
                </div>
                {(openFilterMenu === 'state' || openFilterMenu === 'has_outbreak' || openFilterMenu === 'user_approved' || openFilterMenu === 'is_checked') ? (
                  <select
                    value={filters[openFilterMenu]}
                    onChange={(e) => {
                      handleFilterChange(openFilterMenu, e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '0.9rem',
            border: '1px solid #ddd', 
            borderRadius: '6px',
                      boxSizing: 'border-box',
                      backgroundColor: 'white',
                      color: '#495057',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#adb5bd';
                      e.currentTarget.style.backgroundColor = '#e8ecef';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <option value="">Все</option>
                    {openFilterMenu === 'state' && (
                      <>
                        <option value="санитарное">санитарное</option>
                        <option value="лесопатологическое">лесопатологическое</option>
                      </>
                    )}
                    {(openFilterMenu === 'has_outbreak' || openFilterMenu === 'user_approved' || openFilterMenu === 'is_checked') && (
                      <>
                        <option value="Да">Да</option>
                        <option value="Нет">Нет</option>
                      </>
                    )}
                  </select>
                ) : openFilterMenu === 'executor' ? (
                  <ChecklistFilter
                    options={uniqueExecutors}
                    selected={Array.isArray(filters.executor) ? filters.executor : []}
                    onChange={(next) => handleFilterChange('executor', next)}
                    placeholder="Поиск ФИО..."
                    emptyText="Нет исполнителей"
                  />
                ) : openFilterMenu === 'main_reason' ? (
                  <div>
                    {/* Поле поиска для фильтрации списка причин */}
                    <input
                      type="text"
                      placeholder="Поиск причины..."
                      value={reasonSearchText}
                      onChange={(e) => setReasonSearchText(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '0.9rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#495057',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        marginBottom: '12px'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#adb5bd';
                        e.currentTarget.style.backgroundColor = '#e8ecef';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#ddd';
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    />
                    {(() => {
                      // Фильтруем причины по поисковому запросу с умной сортировкой
                      const filteredReasons = smartFilter(uniqueReasons, reasonSearchText);
                      
                      // Проверяем, все ли отфильтрованные причины выбраны
                      const allFilteredSelected = filteredReasons.length > 0 && 
                        filteredReasons.every(reason => filters.main_reason.includes(reason));
                      
                      return (
                        <>
                          {/* Чекбокс "Выбрать все" - вне прокручиваемой области */}
                          {filteredReasons.length > 0 && (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                                marginBottom: '8px',
                                borderBottom: '1px solid #ddd',
                                paddingBottom: '12px',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Выбираем все отфильтрованные причины
                                    const newReasons = [...new Set([...filters.main_reason, ...filteredReasons])];
                                    handleFilterChange('main_reason', newReasons);
                                  } else {
                                    // Убираем все отфильтрованные причины
                                    const newReasons = filters.main_reason.filter(reason => 
                                      !filteredReasons.includes(reason)
                                    );
                                    handleFilterChange('main_reason', newReasons);
                                  }
                                }}
                                style={{
                                  marginRight: '8px',
                                  cursor: 'pointer',
                                  width: '18px',
                                  height: '18px',
                                  minWidth: '18px',
                                  minHeight: '18px',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{ fontSize: '0.9rem', color: '#495057', fontWeight: '500' }}>
                                Выбрать все ({filteredReasons.length})
                              </span>
                            </label>
                          )}
                          {/* Список причин с чекбоксами - прокручиваемая область */}
                          <div 
                            className="custom-scrollbar"
                            style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              padding: '8px',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f1f5f9'
                            }}
                          >
                            {filteredReasons.length > 0 ? (
                              filteredReasons.map((reason, index) => (
                                <label
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s',
                                    marginBottom: '4px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={filters.main_reason.includes(reason)}
                                    onChange={(e) => handleReasonFilterChange(reason, e.target.checked)}
                                    style={{
                                      marginRight: '8px',
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                      minWidth: '18px',
                                      minHeight: '18px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>
                                    {reason || '-'}
                                  </span>
                                </label>
                              ))
                            ) : (
                              <div style={{ padding: '8px', color: '#999', fontSize: '0.9rem' }}>
                                {reasonSearchText ? 'Ничего не найдено' : 'Нет доступных причин'}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                    {/* Показываем количество выбранных причин */}
                    {filters.main_reason.length > 0 && (
                      <div style={{ 
                        marginTop: '12px', 
                        fontSize: '0.85rem', 
                        color: '#495057',
                        padding: '8px',
                        backgroundColor: '#e8ecef',
                        borderRadius: '4px'
                      }}>
                        Выбрано: {filters.main_reason.length}
                      </div>
                    )}
                  </div>
                ) : openFilterMenu === 'dominant_species' ? (
                  <div>
                    {/* Поле поиска для фильтрации списка пород */}
                    <input
                      type="text"
                      placeholder="Поиск породы..."
                      value={speciesSearchText}
                      onChange={(e) => setSpeciesSearchText(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '0.9rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#495057',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        marginBottom: '12px'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#adb5bd';
                        e.currentTarget.style.backgroundColor = '#e8ecef';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#ddd';
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    />
                    {(() => {
                      // Фильтруем породы по поисковому запросу с умной сортировкой
                      const filteredSpecies = smartFilter(uniqueSpecies, speciesSearchText);
                      
                      // Проверяем, все ли отфильтрованные породы выбраны
                      const allFilteredSelected = filteredSpecies.length > 0 && 
                        filteredSpecies.every(species => filters.dominant_species.includes(species));
                      
                      return (
                        <>
                          {/* Чекбокс "Выбрать все" - вне прокручиваемой области */}
                          {filteredSpecies.length > 0 && (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                                marginBottom: '8px',
                                borderBottom: '1px solid #ddd',
                                paddingBottom: '12px',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Выбираем все отфильтрованные породы
                                    const newSpecies = [...new Set([...filters.dominant_species, ...filteredSpecies])];
                                    handleFilterChange('dominant_species', newSpecies);
                                  } else {
                                    // Убираем все отфильтрованные породы
                                    const newSpecies = filters.dominant_species.filter(species => 
                                      !filteredSpecies.includes(species)
                                    );
                                    handleFilterChange('dominant_species', newSpecies);
                                  }
                                }}
                                style={{
                                  marginRight: '8px',
                                  cursor: 'pointer',
                                  width: '18px',
                                  height: '18px',
                                  minWidth: '18px',
                                  minHeight: '18px',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{ fontSize: '0.9rem', color: '#495057', fontWeight: '500' }}>
                                Выбрать все ({filteredSpecies.length})
                              </span>
                            </label>
                          )}
                          {/* Список пород с чекбоксами - прокручиваемая область */}
                          <div 
                            className="custom-scrollbar"
                            style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              padding: '8px',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f1f5f9'
                            }}
                          >
                            {filteredSpecies.length > 0 ? (
                              filteredSpecies.map((species, index) => (
                                <label
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s',
                                    marginBottom: '4px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={filters.dominant_species.includes(species)}
                                    onChange={(e) => handleSpeciesFilterChange(species, e.target.checked)}
                                    style={{
                                      marginRight: '8px',
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                      minWidth: '18px',
                                      minHeight: '18px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>
                                    {species || '-'}
                                  </span>
                                </label>
                              ))
                            ) : (
                              <div style={{ padding: '8px', color: '#999', fontSize: '0.9rem' }}>
                                {speciesSearchText ? 'Ничего не найдено' : 'Нет доступных пород'}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                    {/* Показываем количество выбранных пород */}
                    {filters.dominant_species.length > 0 && (
                      <div style={{ 
                        marginTop: '12px', 
                        fontSize: '0.85rem', 
                        color: '#495057',
                        padding: '8px',
                        backgroundColor: '#e8ecef',
                        borderRadius: '4px'
                      }}>
                        Выбрано: {filters.dominant_species.length}
                      </div>
                    )}
                  </div>
                ) : openFilterMenu === 'tax_area' ? (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontSize: '0.85rem', 
                          color: '#495057',
                          fontWeight: '500'
                        }}>
                          От:
                        </label>
                        <DecimalInput
                          placeholder="Минимум"
                          value={filters.tax_area.min || ''}
                          onChange={(e) => {
                            handleFilterChange('tax_area', {
                              ...filters.tax_area,
                              min: toDecimalPoint(e.target.value)
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxSizing: 'border-box',
                            backgroundColor: 'white',
                            color: '#495057',
                            outline: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#adb5bd';
                            e.currentTarget.style.backgroundColor = '#e8ecef';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontSize: '0.85rem', 
                          color: '#495057',
                          fontWeight: '500'
                        }}>
                          До:
                        </label>
                        <DecimalInput
                          placeholder="Максимум"
                          value={filters.tax_area.max || ''}
                          onChange={(e) => {
                            handleFilterChange('tax_area', {
                              ...filters.tax_area,
                              max: toDecimalPoint(e.target.value)
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxSizing: 'border-box',
                            backgroundColor: 'white',
                            color: '#495057',
                            outline: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#adb5bd';
                            e.currentTarget.style.backgroundColor = '#e8ecef';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : openFilterMenu === 'date' ? (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontSize: '0.85rem', 
                          color: '#495057',
                          fontWeight: '500'
                        }}>
                          От:
                        </label>
                        <input
                          type="date"
                          value={filters.date.min || ''}
                          onChange={(e) => {
                            handleFilterChange('date', {
                              ...filters.date,
                              min: e.target.value
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxSizing: 'border-box',
                            backgroundColor: 'white',
                            color: '#495057',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#adb5bd';
                            e.currentTarget.style.backgroundColor = '#e8ecef';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '6px', 
                          fontSize: '0.85rem', 
                          color: '#495057',
                          fontWeight: '500'
                        }}>
                          До:
                        </label>
                        <input
                          type="date"
                          value={filters.date.max || ''}
                          onChange={(e) => {
                            handleFilterChange('date', {
                              ...filters.date,
                              max: e.target.value
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxSizing: 'border-box',
                            backgroundColor: 'white',
                            color: '#495057',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#adb5bd';
                            e.currentTarget.style.backgroundColor = '#e8ecef';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : openFilterMenu === 'address' ? (
                  <div 
                    className="custom-scrollbar"
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '24px', 
                      maxHeight: '600px', 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      paddingRight: '8px'
                    }}
                  >
                    {/* Колонка 1: Субъект, Участковое лесничество, Квартал */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <AddressFilterList
                        title="Субъект:"
                        placeholder="Поиск субъекта..."
                        part="subject"
                        options={allUniqueAddressParts.subjects}
                        selected={filters.address.subject}
                        onChangePart={handleAddressPartChange}
                        emptyText="Нет доступных субъектов"
                      />
                      <AddressFilterList
                        title="Участковое лесничество:"
                        placeholder="Поиск участкового лесничества..."
                        part="district_forestry"
                        options={allUniqueAddressParts.districtForestries}
                        selected={filters.address.district_forestry}
                        onChangePart={handleAddressPartChange}
                        emptyText="Нет доступных участковых лесничеств"
                      />
                      <AddressFilterList
                        title="Квартал:"
                        placeholder="Поиск квартала..."
                        part="kv"
                        options={allUniqueAddressParts.kvs}
                        selected={filters.address.kv}
                        onChangePart={handleAddressPartChange}
                        emptyText="Нет доступных кварталов"
                      />
                    </div>
                    {/* Колонка 2: Лесничество, Выдел */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <AddressFilterList
                        title="Лесничество:"
                        placeholder="Поиск лесничества..."
                        part="forestry"
                        options={allUniqueAddressParts.forestries}
                        selected={filters.address.forestry}
                        onChangePart={handleAddressPartChange}
                        emptyText="Нет доступных лесничеств"
                      />
                      <AddressFilterList
                        title="Выдел:"
                        placeholder="Поиск выдела..."
                        part="vid"
                        options={allUniqueAddressParts.vids}
                        selected={filters.address.vid}
                        onChangePart={handleAddressPartChange}
                        emptyText="Нет доступных выделов"
                      />
                    </div>
                    {/* Площадь лесопатологического выдела - на всю ширину */}
                    <div style={{ 
                      gridColumn: '1 / -1',
                      backgroundColor: '#fafafa', 
                      border: '1px solid #e9ecef', 
                      borderRadius: '6px', 
                      padding: '12px',
                      transition: 'all 0.2s ease'
                    }}>
                      <div style={{ marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
                        Площадь лесопатологического выдела:
                      </div>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '6px', 
                            fontSize: '0.85rem', 
                            color: '#495057',
                            fontWeight: '500'
                          }}>
                            От:
                          </label>
                          <DecimalInput
                            placeholder="Минимум"
                            value={filters.address.slp.min || ''}
                            onChange={(e) => {
                              handleFilterChange('address', {
                                ...filters.address,
                                slp: {
                                  ...filters.address.slp,
                                  min: toDecimalPoint(e.target.value)
                                }
                              });
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '0.9rem',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              boxSizing: 'border-box',
                              backgroundColor: 'white',
                              color: '#495057',
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#adb5bd';
                              e.currentTarget.style.backgroundColor = '#e8ecef';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#ddd';
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '6px', 
                            fontSize: '0.85rem', 
                            color: '#495057',
                            fontWeight: '500'
                          }}>
                            До:
                          </label>
                          <DecimalInput
                            placeholder="Максимум"
                            value={filters.address.slp.max || ''}
                            onChange={(e) => {
                              handleFilterChange('address', {
                                ...filters.address,
                                slp: {
                                  ...filters.address.slp,
                                  max: toDecimalPoint(e.target.value)
                                }
                              });
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: '0.9rem',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              boxSizing: 'border-box',
                              backgroundColor: 'white',
                              color: '#495057',
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#adb5bd';
                              e.currentTarget.style.backgroundColor = '#e8ecef';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#ddd';
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Введите значение для фильтрации..."
                    value={filters[openFilterMenu]}
                    onChange={(e) => handleFilterChange(openFilterMenu, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '0.9rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                      backgroundColor: 'white',
                      color: '#495057',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#adb5bd';
                      e.currentTarget.style.backgroundColor = '#e8ecef';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  />
                )}
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      if (openFilterMenu === 'main_reason') {
                        handleFilterChange('main_reason', []);
                        setReasonSearchText('');
                      } else if (openFilterMenu === 'dominant_species') {
                        handleFilterChange('dominant_species', []);
                        setSpeciesSearchText('');
                      } else if (openFilterMenu === 'executor') {
                        handleFilterChange('executor', []);
                      } else if (openFilterMenu === 'tax_area') {
                        handleFilterChange('tax_area', { min: '', max: '' });
                      } else if (openFilterMenu === 'date') {
                        handleFilterChange('date', { min: '', max: '' });
                      } else if (openFilterMenu === 'address') {
                        handleFilterChange('address', defaultFilters.address);
                      } else {
                        handleFilterChange(openFilterMenu, '');
                      }
                      setOpenFilterMenu(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e8ecef';
                      e.currentTarget.style.borderColor = '#adb5bd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#ddd';
                    }}
                  >
                    Очистить
                  </button>
                  <button
                    onClick={() => {
                      setOpenFilterMenu(null);
                      if (openFilterMenu === 'main_reason') {
                        setReasonSearchText('');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e8ecef';
                      e.currentTarget.style.borderColor = '#adb5bd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#ddd';
                    }}
                  >
                    Применить
                  </button>
                </div>
              </div>
            )}
            {/* Модальное окно подтверждения удаления карточки (в стиле переключателя форм 4/5) */}
            {showDeleteConfirm && pendingDeleteReport && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
                    Удаление карточки
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
                    Удалить карточку «{pendingDeleteReport.name}»? Отчёт будет перемещён в удалённые.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={cancelDeleteReport}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e9ecef';
                        e.target.style.color = '#495057';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.color = '#6c757d';
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={performDeleteReport}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#5a6268';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#6c757d';
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Модальное окно подтверждения одобрения карточки (в стиле системы) */}
            {showApproveConfirm && pendingApproveReport && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
                    {pendingApproveReport.kind === 'user'
                      ? 'Одобрение пользователя'
                      : (pendingApproveReport.kind === 'both' ? 'Одобрение карточки' : 'Одобрение филиала')}
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
                    {pendingApproveReport.kind === 'user' ? (
                      <>
                        Одобрить карточку «{pendingApproveReport.name}»? После одобрения карточка
                        станет видна филиалу, а вам — доступна только для просмотра (без редактирования).
                      </>
                    ) : pendingApproveReport.kind === 'both' ? (
                      <>
                        Одобрить карточку «{pendingApproveReport.name}»? Будет сразу проставлено
                        одобрение пользователя и одобрение филиала. После этого карточка начнёт
                        участвовать в отчётах.
                      </>
                    ) : (
                      <>
                        Одобрить карточку «{pendingApproveReport.name}» от филиала? Только после этого она
                        начнёт участвовать в отчётах.
                      </>
                    )}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={cancelApproveReport}
                      disabled={isApproving}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        borderRadius: '4px',
                        cursor: isApproving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={performApproveReport}
                      disabled={isApproving}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: isApproving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {isApproving ? 'Одобрение...' : 'Согласен'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showRevertConfirm && pendingRevertReport && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '16px', fontWeight: '600' }}>
                    {pendingRevertReport.kind === 'user'
                      ? (pendingRevertReport.isOwner ? 'Отзыв карточки из согласования' : 'Отклонение карточки')
                      : 'Отмена одобрения филиала'}
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
                    {pendingRevertReport.kind === 'user' ? (
                      pendingRevertReport.isOwner ? (
                        <>
                          Отозвать карточку «{pendingRevertReport.name}» из согласования?
                          Карточка снова станет доступна для редактирования.
                        </>
                      ) : (
                        <>
                          Отклонить карточку «{pendingRevertReport.name}» и вернуть на доработку владельцу?
                          Карточка снова станет доступна для редактирования владельцу.
                        </>
                      )
                    ) : (
                      <>
                        Отменить одобрение филиала у карточки «{pendingRevertReport.name}»?
                        Карточка перестанет участвовать в отчётах.
                      </>
                    )}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={cancelRevertReport}
                      disabled={isReverting}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        borderRadius: '4px',
                        cursor: isReverting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={performRevertReport}
                      disabled={isReverting}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: isReverting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {isReverting ? 'Отмена одобрения...' : 'Согласен'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {approveBlocked && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }} onClick={() => setApproveBlocked(null)}>
                <div className="modal-content" style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  maxWidth: '520px',
                  width: '90%',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 15px 0', color: '#c0392b', fontSize: '16px', fontWeight: '600' }}>
                    Карточку нельзя одобрить
                  </h3>
                  <p style={{ margin: '0 0 12px 0', color: '#6c757d', lineHeight: '1.5', fontSize: '14px' }}>
                    Карточка «{approveBlocked.name}» заполнена не полностью или с ошибками.
                    Исправьте их и сохраните карточку, прежде чем одобрять{approveBlocked.errors.length ? ':' : '.'}
                  </p>
                  {approveBlocked.errors.length > 0 && (
                    <ul style={{
                      margin: '0 0 20px 0',
                      paddingLeft: '20px',
                      color: '#6c757d',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      maxHeight: '260px',
                      overflowY: 'auto'
                    }}>
                      {approveBlocked.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setApproveBlocked(null)}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #6c757d',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Понятно
                    </button>
                  </div>
                </div>
              </div>
            )}
            {journalTableElement}
            {canApprove && selectedForApproval.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                {bulkApproveButton}
              </div>
            )}
            {dupTooltip && (
              <div
                style={{
                  position: 'fixed',
                  left: Math.min(dupTooltip.x + 14, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 320),
                  top: dupTooltip.y + 16,
                  zIndex: 2000,
                  maxWidth: '300px',
                  background: '#fff',
                  border: '1px solid #f0c2c7',
                  borderLeft: '3px solid #dc3545',
                  borderRadius: '6px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  padding: '8px 12px',
                  fontSize: '12.5px',
                  color: '#6c2b32',
                  lineHeight: '1.45',
                  pointerEvents: 'none',
                }}
              >
                В этой же организации есть другая карточка с такими же реквизитами
                (субъект, организация, лесничество, уч. лесничество, урочище, квартал,
                кадастровый номер, лесотаксационный и лесопатологический выделы) и тем же
                месяцем даты проведения ВНН. Возможный дубликат.
              </div>
            )}
          </div>
        )}
      </div>
      )
      ) : (
      <div
        ref={reportsContentRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          paddingTop: '40px',
        }}
      >
        <p className="sz-1-3 mt0 b-7" style={{ margin: 0, padding: 0, marginBottom: '48px' }}>{listHeading}</p>

        <div className="template-section">
          <div className="template-content">
            <div style={{ marginBottom: '28px', marginTop: '-10px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label
                  htmlFor="report-form-type-combo-input"
                  className="sz-0-9 b-5"
                  style={{ margin: 0, padding: 0, color: '#495057' }}
                >
                  Какой отчёт сформировать <span className="required">*</span>
                </label>
                <button
                  type="button"
                  onClick={clearReportFormTypeBlock}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Очистить
                </button>
              </div>
              <div ref={reportFormComboRef} style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6c757d',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                    aria-hidden
                  >
                    <Icon24Search width={20} height={20} />
                  </span>
                  <input
                    id="report-form-type-combo-input"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    role="combobox"
                    aria-expanded={reportFormComboOpen}
                    aria-controls="report-form-type-listbox"
                    aria-autocomplete="list"
                    placeholder="Поиск по названию отчёта…"
                    value={
                      reportFormComboOpen
                        ? reportFormFilter
                        : getReportFormTypeLabel(selectedReportFormType)
                    }
                    onChange={(e) => {
                      setReportFormFilter(e.target.value);
                      setReportFormComboOpen(true);
                    }}
                    onFocus={() => {
                      if (!reportFormComboOpen) {
                        setReportFormFilter('');
                      }
                      setReportFormComboOpen(true);
                    }}
                    className={`form-control ${!selectedReportFormType ? 'required-field' : ''}`}
                    style={{
                      width: '100%',
                      minHeight: '44px',
                      padding: '10px 14px 10px 44px',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      color: '#495057',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                    }}
                  />
                </div>
                {reportFormComboOpen && (
                  <ul
                    id="report-form-type-listbox"
                    role="listbox"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '100%',
                      marginTop: '6px',
                      marginBottom: 0,
                      padding: '6px 0',
                      maxHeight: 'min(50vh, 300px)',
                      overflowY: 'auto',
                      listStyle: 'none',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      zIndex: 200,
                    }}
                  >
                    {filteredReportFormOptions.length === 0 ? (
                      <li
                        style={{
                          padding: '12px 16px',
                          color: '#6c757d',
                          fontSize: '0.9rem',
                        }}
                      >
                        Ничего не найдено
                      </li>
                    ) : (
                      filteredReportFormOptions.map((opt, optIndex, optList) => (
                        <React.Fragment key={opt.value || 'placeholder'}>
                          {opt.group &&
                            (optIndex === 0 || optList[optIndex - 1].group !== opt.group) && (
                              <li
                                style={{
                                  padding: '8px 16px 4px',
                                  fontSize: '0.78rem',
                                  fontWeight: '700',
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                  color: '#6c757d',
                                  backgroundColor: '#f8f9fa',
                                }}
                              >
                                {opt.group}
                              </li>
                            )}
                        <li
                          role="option"
                          aria-selected={selectedReportFormType === opt.value}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedReportFormType(opt.value);
                            clearGeneratedReport();
                            setReportFormComboOpen(false);
                            setReportFormFilter('');
                            if (!isGlpmParamsReportType(opt.value)) {
                              setGlpmMonthlyReportMonth('');
                              setGlpmPickerFormData({ srf: '', filial: '' });
                            }
                            if (opt.value !== GLPM_YEARLY_PLANNED_VOLUMES_TYPE) {
                              setGlpmYearlyReportYear(new Date().getFullYear());
                            }
                            if (opt.value !== GLPM_PLANNED_VOLUMES_EXECUTION_TYPE) {
                              setGlpmExecutionQuarter('');
                              setGlpmExecutionYear(new Date().getFullYear());
                            }
                            if (opt.value !== COORDINATES_TYPE) {
                              setCoordinatesYear(new Date().getFullYear());
                            }
                            if (opt.value !== VNVO_OKOLOT_TYPE) {
                              setVnvoOkolotYear(new Date().getFullYear());
                            }
                            if (opt.value !== GLPM_WOOD_STAND_DESCRIPTION_TYPE) {
                              setGlpmWoodStandYear(new Date().getFullYear());
                            }
                          }}
                          style={{
                            padding: '11px 16px',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            color: '#495057',
                            cursor: 'pointer',
                            lineHeight: 1.35,
                            backgroundColor:
                              selectedReportFormType === opt.value ? '#e8ecef' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (selectedReportFormType !== opt.value) {
                              e.currentTarget.style.backgroundColor = '#f1f3f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              selectedReportFormType === opt.value ? '#e8ecef' : 'transparent';
                          }}
                        >
                          {opt.label}
                        </li>
                        </React.Fragment>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>

          </div>

            {selectedReportFormType === GLPM_MONTHLY_PLANNED_VOLUMES_TYPE && (
              <GlpmReportParamsSection
                idPrefix="glpm_monthly"
                {...glpmReportParamsSectionProps}
                canGenerate={canSubmitGlpmMonthlyReport}
                canDownload={generatedReport?.reportType === GLPM_MONTHLY_PLANNED_VOLUMES_TYPE}
                generating={glpmMonthlyGenerating}
                onGenerate={handleGlpmMonthlyGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === GLPM_MONTHLY_PLANNED_VOLUMES_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {selectedReportFormType === GLPM_YEARLY_PLANNED_VOLUMES_TYPE && (
              <GlpmReportParamsSection
                idPrefix="glpm_yearly"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={glpmYearlyReportYear}
                onYearChange={setGlpmYearlyReportYear}
                onClearParams={clearGlpmYearlyReport}
                canGenerate={canSubmitGlpmYearlyReport}
                canDownload={generatedReport?.reportType === GLPM_YEARLY_PLANNED_VOLUMES_TYPE}
                generating={glpmYearlyGenerating}
                onGenerate={handleGlpmYearlyGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === GLPM_YEARLY_PLANNED_VOLUMES_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {selectedReportFormType === GLPM_PLANNED_VOLUMES_EXECUTION_TYPE && (
              <GlpmReportParamsSection
                idPrefix="glpm_planned_execution"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showQuarter
                quarter={glpmExecutionQuarter}
                onQuarterChange={setGlpmExecutionQuarter}
                showYear
                year={glpmExecutionYear}
                onYearChange={setGlpmExecutionYear}
                onClearParams={clearGlpmExecutionReport}
                canGenerate={canSubmitGlpmPlannedVolumesExecution}
                canDownload={generatedReport?.reportType === GLPM_PLANNED_VOLUMES_EXECUTION_TYPE}
                generating={glpmExecutionGenerating}
                onGenerate={handleGlpmPlannedVolumesExecutionGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === GLPM_PLANNED_VOLUMES_EXECUTION_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {selectedReportFormType === GLPM_WOOD_STAND_DESCRIPTION_TYPE && (
              <GlpmReportParamsSection
                idPrefix="glpm_wood_stand"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={glpmWoodStandYear}
                onYearChange={setGlpmWoodStandYear}
                onClearParams={clearGlpmWoodStandReport}
                canGenerate={canGenerateGlpmWoodStand}
                canDownload={generatedReport?.reportType === GLPM_WOOD_STAND_DESCRIPTION_TYPE}
                generating={glpmWoodStandDownloading}
                onGenerate={handleGlpmWoodStandGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === GLPM_WOOD_STAND_DESCRIPTION_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {isTabl1FamilyType(selectedReportFormType) && (
              <GlpmReportParamsSection
                idPrefix="tabl1"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={tabl1Year}
                onYearChange={setTabl1Year}
                onClearParams={clearTabl1Report}
                canGenerate={canGenerateTabl1}
                canDownload={generatedReport?.reportType === selectedReportFormType}
                generating={tabl1Downloading}
                onGenerate={handleTabl1GenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === selectedReportFormType
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {selectedReportFormType === TABL1_FULL_TYPE && canUseTabl1FullExport(user) && (
              <GlpmReportParamsSection
                idPrefix="tabl1_full"
                {...glpmReportParamsSectionProps}
                showParams={false}
                onClearParams={clearGeneratedReport}
                canGenerate
                canDownload={generatedReport?.reportType === TABL1_FULL_TYPE}
                generating={tabl1FullDownloading}
                onGenerate={handleTabl1FullGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === TABL1_FULL_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              >
                <p style={{ margin: 0, color: '#495057', fontSize: '14px' }}>
                  В книгу попадают все одобренные карточки, доступные вам, — по всем
                  субъектам РФ и за все годы. Строки идут по субъектам, внутри — по
                  дате проведения ВНН.
                </p>
              </GlpmReportParamsSection>
            )}
            {selectedReportFormType === COORDINATES_TYPE && (
              <GlpmReportParamsSection
                idPrefix="coordinates"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={coordinatesYear}
                onYearChange={setCoordinatesYear}
                onClearParams={clearCoordinatesReport}
                canGenerate={canGenerateCoordinates}
                canDownload={generatedReport?.reportType === COORDINATES_TYPE}
                generating={coordinatesDownloading}
                onGenerate={handleCoordinatesGenerateReport}
                onDownload={downloadGeneratedReport}
                reportPreview={
                  generatedReport?.reportType === COORDINATES_TYPE
                    ? <ReportPreview report={generatedReport} />
                    : null
                }
              />
            )}
            {selectedReportFormType === VNVO_OKOLOT_TYPE && (
              <GlpmReportParamsSection
                idPrefix="vnvo_okolot"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={vnvoOkolotYear}
                onYearChange={setVnvoOkolotYear}
                onClearParams={clearVnvoOkolotReport}
                canGenerate={canGenerateVnvoOkolot}
                canDownload={generatedReport?.reportType === VNVO_OKOLOT_TYPE}
                generating={vnvoOkolotDownloading}
                onGenerate={handleVnvoOkolotGenerateReport}
                onDownload={downloadGeneratedReport}
              />
            )}
            {selectedReportFormType === VNVO_KLADKI_STVOL_TYPE && (
              <GlpmReportParamsSection
                idPrefix="vnvo_kladki_stvol"
                {...glpmReportParamsSectionProps}
                showMonth={false}
                showYear
                year={vnvoKladkiYear}
                onYearChange={setVnvoKladkiYear}
                onClearParams={clearVnvoKladkiReport}
                canGenerate={canGenerateVnvoKladki}
                canDownload={generatedReport?.reportType === VNVO_KLADKI_STVOL_TYPE}
                generating={vnvoKladkiDownloading}
                onGenerate={handleVnvoKladkiGenerateReport}
                onDownload={downloadGeneratedReport}
              />
            )}
        </div>
      </div>
      )}
      <PickerModal
        showPicker={showPicker}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        getFilteredOptions={getFilteredOptions}
        getSelectedIndexInFiltered={getSelectedIndexInFiltered}
        pickerValue={pickerValue}
        selectPickerValue={selectPickerValue}
        closePicker={closePicker}
      />
    </div>
  );
};

export default DocumentsPage;
